import React, { useEffect, useLayoutEffect, useState, useCallback, useMemo, useRef, lazy, Suspense } from 'react';
import { createPortal } from 'react-dom';
import { useVirtualizer, useWindowVirtualizer } from '@tanstack/react-virtual';
import '../styles/Leads.css';
// VerifyProperty is rendered full-screen as the second step of the "+ New
// lead" flow. Direct import (not lazy) — RunComps.js does the same. Lazy
// from a second entry point conflicts with LeadsFilterModal's CSS chunk
// ordering under CI=true (mini-css-extract-plugin chunk-conflict errors).
// VerifyProperty is already chunked for the /verify-property route, so the
// real bundle hit is just shared-module overhead webpack already dedupes.
import VerifyProperty from '../pages/VerifyProperty';
import { checkOnAuthStateChanged, cloudFunctionV2, formatPhoneNumber, getDocument, recordEvent, setSubData } from '../functions';
import { Gmail, Messages, Outlook, Twilio } from '../assets/icons';
import { useQueryClient } from '@tanstack/react-query';
import { useLeadsBootstrap, useLeadsPage, useLeadsInBounds, buildLeadsParams, isSortableColumn, MAP_VIEWPORT_LIMIT } from '../hooks/useLeadsQuery';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loading, Pagination, ColumnToggle, GoogleMapsLoader, LeadsMap, LeadCard, Modal, ExitScreen, ScrollableImages, ImageGallery } from '../components';
import { useImageViewer } from '../hooks/useModals';
import { useAddressAutocomplete, useProcessingLeads } from '../hooks';
import LeadsTable, { STANDARD_COLUMNS, gapAfterAskEdit } from '../components/LeadsTable';
import InProgressLeadsBadge from '../components/InProgressLeadsBadge';
import LeadsActivityRail, { useActivityRailState } from '../components/LeadsActivityRail';
import { useCompany, useUser, useUI } from '../contexts';
import { arrayMove } from '@dnd-kit/sortable';
import { getFirestore, doc, updateDoc, deleteField, onSnapshot } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { HugeiconsIcon } from '@hugeicons/react';
import { PlusSignIcon, FilterHorizontalIcon, SidebarRightIcon, Cancel01Icon, ArrowDown01Icon, LeftToRightListBulletIcon, Location01Icon, Mail01Icon, Search01Icon } from '@hugeicons/core-free-icons';
import { colour, numberFormatter } from '../styles/GlobalStyles';
import { getDealStatuses } from '../utils/dealStatusUtils';
import { isLeadWorking, isCoLivingScorePending } from '../lib/underwritingStatus';
import { getImageFolderProvider, getImageDownloadStageLabel } from '../utils/imageUtils';
import '../styles/LeadsFilterModal.css';

// Modal bodies are lazy-loaded — each ships in its own chunk and only
// downloads on first open. Saves ~50–100 KB from the leads route bundle
// since most users never open all six in a session.
const ImageUrlModal        = lazy(() => import('../modalbody/ImageUrlModal'));
const CreateLeadModal      = lazy(() => import('../modalbody/CreateLeadModal'));
const CreatePortfolioModal = lazy(() => import('../modalbody/CreatePortfolioModal'));
const LeadsFilterModal     = lazy(() => import('../modalbody/LeadsFilterModal'));
const EditAddressModal     = lazy(() => import('../modalbody/EditAddressModal'));
const IdentifyContactModal = lazy(() => import('../modalbody/IdentifyContactModal'));

// The shape detector that used to live here (looksLikeAddressInputForLeads)
// was removed when the modal switched from a single-input + shape heuristic
// to an explicit "Address vs Paste deal" tab pair. Mode is now the gating
// signal — see handleCreateLeadTextChange / createLeadMode.

// Number of leads per page
const PAGE_SIZE = 25;

// Default visible columns.
// ARV and Repair Cost are now default-visible — the at-a-glance signal of
// underwriting health is exactly what the table should surface (their cells
// render tri-state icons via CalcStatus, not raw values). The "hot deal" concept
// (triageAction column, priority Score column, inline flame) was removed entirely;
// deal value now shows as strategy-fit + buyer-match badges under the address.
const DEFAULT_VISIBLE_COLUMNS = ['leadReviewStatus', 'address', 'links', 'beds', 'baths', 'sqft', 'askingPrice', 'zillowEstimate', 'redfinEstimate', 'realtorEstimate', 'arv', 'repairCost', 'propertyType', 'source', 'assigned', 'createdAt', 'updatedAt'];

// Retired triage/hot columns — strip from any saved column prefs so a stale key
// doesn't linger in a user's saved layout after the column was removed from the UI.
const LEGACY_TRIAGE_SCORE_COLUMNS = new Set(['priorityTier', 'triageBucket', 'triageAction', 'priorityScore']);
const filterLegacyTriageColumns = (columnKeys = []) =>
    columnKeys.filter(key => !LEGACY_TRIAGE_SCORE_COLUMNS.has(key));

// Terminal underwriting states — once data.underwriting.status hits one of
// these, the lead is done and the row's ARV/repair cells are settled.
const TERMINAL_UNDERWRITING_STATUSES = new Set(['complete', 'skipped', 'failed', 'rate_limited']);

// True when a lead has status='complete' at the lead level but its
// underwriting hasn't terminally landed yet. This is the window where
// saveLeadBatched has flipped status='complete' (so the queued/processing
// watcher tore down) but processUnderwriting is still running (~7 min for the
// ARV agent). Without watching these, the ARV / repair-cost cells stay red-X
// until manual refresh because nothing else invalidates the leads query.
//
// Worst-case if we misclassify a permanently-no-underwriting lead (e.g. a
// Spencer's-Deals lead saved with underwriting deliberately skipped at queue
// time — the runUnderwriting flag is wiped by saveLeadBatched's full overwrite
// so we can't read it here): the per-lead onSnapshot listener idles harmlessly
// waiting for a write that never comes, and tears down when the lead drops
// out of the page or the user navigates away. No refresh loop.
function isUnderwritingPending(lead) {
    if (!lead) return false;
    if (lead.status !== 'complete') return false;
    if (lead.filtered === true) return false;
    // A bridge deal whose score hasn't landed is NOT finished, whatever
    // underwriting.status says — the score is written in phase 2, after that status
    // has already settled. Checked first so the terminal-status return below can't
    // retire the watcher during exactly the window it is needed. Self-limiting: the
    // helper stops returning true 45 minutes after the lead was created.
    if (isCoLivingScorePending(lead)) return true;
    const uwStatus = lead.underwriting?.status;
    if (uwStatus && TERMINAL_UNDERWRITING_STATUSES.has(uwStatus)) return false;
    return true;
}

// US state abbreviation ↔ full name maps for autocomplete cross-matching
const STATE_ABBR_TO_NAME = {
    al: 'alabama', ak: 'alaska', az: 'arizona', ar: 'arkansas', ca: 'california',
    co: 'colorado', ct: 'connecticut', de: 'delaware', fl: 'florida', ga: 'georgia',
    hi: 'hawaii', id: 'idaho', il: 'illinois', in: 'indiana', ia: 'iowa',
    ks: 'kansas', ky: 'kentucky', la: 'louisiana', me: 'maine', md: 'maryland',
    ma: 'massachusetts', mi: 'michigan', mn: 'minnesota', ms: 'mississippi', mo: 'missouri',
    mt: 'montana', ne: 'nebraska', nv: 'nevada', nh: 'new hampshire', nj: 'new jersey',
    nm: 'new mexico', ny: 'new york', nc: 'north carolina', nd: 'north dakota', oh: 'ohio',
    ok: 'oklahoma', or: 'oregon', pa: 'pennsylvania', ri: 'rhode island', sc: 'south carolina',
    sd: 'south dakota', tn: 'tennessee', tx: 'texas', ut: 'utah', vt: 'vermont',
    va: 'virginia', wa: 'washington', wv: 'west virginia', wi: 'wisconsin', wy: 'wyoming',
    dc: 'district of columbia',
};
const STATE_NAME_TO_ABBR = Object.fromEntries(Object.entries(STATE_ABBR_TO_NAME).map(([a, n]) => [n, a]));

// Cheap client-side check for the "paste is already a clean US street address"
// case (e.g. "4827 N 29th Pl, Phoenix, AZ 85016"). When true, the paste-deal
// flow skips parseLeadText (gpt-4.1-mini) entirely and goes straight to REAPI
// autocomplete + property-details — saves ~2-5s on the common wholesaler
// pattern. False negatives are safe (they fall through to the LLM path); false
// positives surface the existing "couldn't find that address" error via the
// short-circuit autocomplete miss handler.
//
// Strict on purpose: single line, ≤120 chars, leading street number, a
// 2-letter state token. Anything off-pattern (multi-line teasers, prose, bare
// city/state) keeps the LLM path.
const PASTE_BARE_ADDRESS_RX = /^\s*\d{1,6}\s+\S[^,\n]+,?\s+[A-Za-z .'-]+,?\s+[A-Z]{2}(\s+\d{5}(-\d{4})?)?\s*$/;
const looksLikeBareAddress = (text) => {
    if (typeof text !== 'string') return false;
    const t = text.trim();
    if (t.length === 0 || t.length > 120) return false;
    if (t.includes('\n')) return false;
    return PASTE_BARE_ADDRESS_RX.test(t);
};

// Paste-flow staged-progress builder. Returns the 3-step shape consumed by
// CreateLeadModal's `loadingSteps` prop. Step labels are stable across phases;
// only state + optional per-step `detail` text varies.
const PASTE_STEP_LABELS = ['Reading your text', 'Finding the address', 'Loading property details'];
const buildPasteLoadingSteps = (readState, findState, loadState, details = {}) => [
    { label: PASTE_STEP_LABELS[0], state: readState, detail: details.read || null },
    { label: PASTE_STEP_LABELS[1], state: findState, detail: details.find || null },
    { label: PASTE_STEP_LABELS[2], state: loadState, detail: details.load || null }
];

// Lead-type subtitle dropdown — replaces the three "All / Direct to Seller /
// Property Leads" toolbar chips. Lead type is more of a "what view am I
// looking at" choice than a filter the user toggles regularly, so it lives
// next to the page title (Linear / Notion pattern). The active label reads
// like part of the page identity rather than a control to operate.
const LEAD_TYPE_OPTIONS = [
    { value: null,               label: 'All leads' },
    { value: 'direct_to_seller', label: 'Direct to Seller' },
    { value: 'property_lead',    label: 'Property Leads' }
];

// Lead-type tabs — rendered above the toolbar, immediately under the page
// header. One-click segment switching; the active segment has its own
// column order / visibility (see columnOrders map below). Hidden when the
// company only has a single lead type (`showLeadTypeChips`).
function LeadTypeTabs({ value, onChange }) {
    return (
        <div className="leads-type-tabs" role="tablist" aria-label="Lead type">
            {LEAD_TYPE_OPTIONS.map((opt) => {
                const active = opt.value === value;
                return (
                    <button
                        key={opt.value || 'all'}
                        type="button"
                        role="tab"
                        aria-selected={active}
                        className={`leads-type-tab body-regular ${active ? 'leads-type-tab-active text-bold' : 'colour-secondary'}`}
                        onClick={() => onChange(opt.value)}
                    >
                        {opt.label}
                    </button>
                );
            })}
        </div>
    );
}

// Active filter chips — labels + display values for each entry in the
// `appliedFilters` dict. Returns an array of { key, label, removeKeys }
// where removeKeys is the set of dict keys to clear when the chip's × is
// clicked (range filters live as two keys: e.g. priceMin + priceMax).
// Foreclosure / trustee-sale filter options (Tier 1). Canonical, controlled
// vocabulary — the normalizeAuction backend helper writes these exact values.
const AUCTION_STATUS_OPTIONS = [
    { value: 'active', label: 'Active' },
    { value: 'clear_to_bid', label: 'Clear to Bid' },
    { value: 'pending', label: 'Pending' },
    { value: 'postponed', label: 'Postponed' },
    { value: 'cancelled', label: 'Cancelled' },
    { value: 'sold', label: 'Sold' },
];
const LIEN_POSITION_OPTIONS = [
    { value: 1, label: '1st position' },
    { value: 2, label: '2nd position' },
];

// Co-living financing paths — HOW the operator would pay for the house. The values are the
// persisted `data.coLivingFinancingPath` strings written by functions/utils/coLivingCashOutRank.js
// (via coLivingPathSelection.js's PATHS); renaming one here silently stops matching every lead.
// Co-living ONLY: a company that doesn't run it has the field absent on every lead, so the facet
// below comes back empty and the whole section stays hidden.
const FINANCING_PATH_OPTIONS = [
    { value: 'bridgeRefi', label: 'Hard money → refi' },
    { value: 'dscr', label: 'DSCR purchase' },
];

// The financing paths a filter dict is asking for, tolerating the LEGACY single-boolean shape
// (`bridgeRefiOnly: true`) that older bookmarked URLs and stored `filters` params still carry.
// One reader for the chips, the modal seed and the query so the three cannot disagree.
function resolveFinancingPathFilter(appliedFilters = {}) {
    if (Array.isArray(appliedFilters.financingPaths) && appliedFilters.financingPaths.length > 0) {
        return appliedFilters.financingPaths;
    }
    return appliedFilters.bridgeRefiOnly ? ['bridgeRefi'] : [];
}

function buildFilterChips(appliedFilters, dealStatusOptions, availablePortfolios) {
    const chips = [];
    const fmtMoney = (v) => `$${Number(String(v).replace(/[^0-9]/g, '')).toLocaleString()}`;
    const fmtRange = (min, max, formatter = (x) => x) => {
        if (min && max) return `${formatter(min)}–${formatter(max)}`;
        if (min) return `≥ ${formatter(min)}`;
        return `≤ ${formatter(max)}`;
    };
    if (appliedFilters.priceMin || appliedFilters.priceMax) {
        chips.push({ key: 'price', label: `Price: ${fmtRange(appliedFilters.priceMin, appliedFilters.priceMax, fmtMoney)}`, removeKeys: ['priceMin', 'priceMax'] });
    }
    if (appliedFilters.state) chips.push({ key: 'state', label: `State: ${appliedFilters.state}`, removeKeys: ['state'] });
    if (appliedFilters.city) chips.push({ key: 'city', label: `City: ${appliedFilters.city}`, removeKeys: ['city'] });
    if (appliedFilters.bedsMin || appliedFilters.bedsMax) {
        chips.push({ key: 'beds', label: `Beds: ${fmtRange(appliedFilters.bedsMin, appliedFilters.bedsMax)}`, removeKeys: ['bedsMin', 'bedsMax'] });
    }
    if (appliedFilters.bathsMin || appliedFilters.bathsMax) {
        chips.push({ key: 'baths', label: `Baths: ${fmtRange(appliedFilters.bathsMin, appliedFilters.bathsMax)}`, removeKeys: ['bathsMin', 'bathsMax'] });
    }
    if (appliedFilters.sqftMin || appliedFilters.sqftMax) {
        chips.push({ key: 'sqft', label: `Sq ft: ${fmtRange(appliedFilters.sqftMin, appliedFilters.sqftMax, (x) => Number(x).toLocaleString())}`, removeKeys: ['sqftMin', 'sqftMax'] });
    }
    if (appliedFilters.yearBuiltMin || appliedFilters.yearBuiltMax) {
        chips.push({ key: 'yearBuilt', label: `Year: ${fmtRange(appliedFilters.yearBuiltMin, appliedFilters.yearBuiltMax)}`, removeKeys: ['yearBuiltMin', 'yearBuiltMax'] });
    }
    if (appliedFilters.spreadMin) chips.push({ key: 'spread', label: `Spread ≥ ${appliedFilters.spreadMin}%`, removeKeys: ['spreadMin'] });
    // Our price's gap, not the flip MAO's — the neighbouring column is labelled "Flip MAO".
    if (appliedFilters.maoVsAskingMax) chips.push({ key: 'maoVsAsking', label: `Our price vs asking ≤ ${appliedFilters.maoVsAskingMax}%`, removeKeys: ['maoVsAskingMax'] });
    // Both keys are cleared together: the chip has to be removable whether it came from the
    // current multi-select or a legacy `bridgeRefiOnly` URL, or the × would leave the filter on.
    const financingPaths = resolveFinancingPathFilter(appliedFilters);
    if (financingPaths.length > 0) {
        const labels = financingPaths.map((v) => FINANCING_PATH_OPTIONS.find((o) => o.value === v)?.label || v);
        chips.push({ key: 'financingPaths', label: `Financing: ${labels.join(', ')}`, removeKeys: ['financingPaths', 'bridgeRefiOnly'] });
    }
    if (appliedFilters.dealScoreMin) chips.push({ key: 'dealScore', label: `Deal score ≥ ${appliedFilters.dealScoreMin}`, removeKeys: ['dealScoreMin'] });
    if (appliedFilters.dateMin || appliedFilters.dateMax) {
        chips.push({ key: 'date', label: `Date: ${fmtRange(appliedFilters.dateMin, appliedFilters.dateMax)}`, removeKeys: ['dateMin', 'dateMax'] });
    }
    if (Array.isArray(appliedFilters.propertyType) && appliedFilters.propertyType.length > 0) {
        chips.push({ key: 'propertyType', label: `Property type: ${appliedFilters.propertyType.length === 1 ? appliedFilters.propertyType[0] : `${appliedFilters.propertyType.length} selected`}`, removeKeys: ['propertyType'] });
    }
    if (Array.isArray(appliedFilters.assignees) && appliedFilters.assignees.length > 0) {
        chips.push({ key: 'assignees', label: `Assignees · ${appliedFilters.assignees.length}`, removeKeys: ['assignees'] });
    }
    if (Array.isArray(appliedFilters.dealStatuses) && appliedFilters.dealStatuses.length > 0) {
        const labels = appliedFilters.dealStatuses
            .map((v) => (dealStatusOptions || []).find((o) => o.value === v)?.label || v)
            .filter(Boolean);
        chips.push({ key: 'dealStatuses', label: `Status: ${labels.length === 1 ? labels[0] : `${labels.length} selected`}`, removeKeys: ['dealStatuses'] });
    }
    if (Array.isArray(appliedFilters.senders) && appliedFilters.senders.length > 0) {
        chips.push({ key: 'senders', label: `Senders · ${appliedFilters.senders.length}`, removeKeys: ['senders'] });
    }
    if (Array.isArray(appliedFilters.portfolios) && appliedFilters.portfolios.length > 0) {
        const names = appliedFilters.portfolios.map(id => (availablePortfolios || []).find(p => p.id === id)?.name || id);
        chips.push({ key: 'portfolios', label: `Portfolio: ${names.length === 1 ? names[0] : `${names.length} selected`}`, removeKeys: ['portfolios'] });
    }
    if (Array.isArray(appliedFilters.auctionStatus) && appliedFilters.auctionStatus.length > 0) {
        const labels = appliedFilters.auctionStatus
            .map((v) => AUCTION_STATUS_OPTIONS.find((o) => o.value === v)?.label || v)
            .filter(Boolean);
        chips.push({ key: 'auctionStatus', label: `Auction: ${labels.length === 1 ? labels[0] : `${labels.length} selected`}`, removeKeys: ['auctionStatus'] });
    }
    if (Array.isArray(appliedFilters.lienPosition) && appliedFilters.lienPosition.length > 0) {
        const labels = appliedFilters.lienPosition
            .map((v) => LIEN_POSITION_OPTIONS.find((o) => o.value === Number(v))?.label || v)
            .filter(Boolean);
        chips.push({ key: 'lienPosition', label: `Lien: ${labels.join(', ')}`, removeKeys: ['lienPosition'] });
    }
    if (appliedFilters.auctionDateMin || appliedFilters.auctionDateMax) {
        chips.push({ key: 'auctionDate', label: `Auction date: ${fmtRange(appliedFilters.auctionDateMin, appliedFilters.auctionDateMax)}`, removeKeys: ['auctionDateMin', 'auctionDateMax'] });
    }
    return chips;
}

function Leads() {
    const [loggedIn, setLoggedIn] = useState(null);
    const [loading, setLoading] = useState(true);
    const [leads, setLeads] = useState([]);
    const [leadsTotal, setLeadsTotal] = useState(0);
    const [searchParams, setSearchParams] = useSearchParams();
    // The ordering chosen FOR THIS VISIT — a `sort` in the URL (bookmark or shared link) or a
    // header click. `null` means nobody chose, and the company default fills in during render
    // (see `sortColumn` below, after useCompany).
    //
    // ⚠️ DERIVED, NOT APPLIED IN AN EFFECT. An effect that set the default after companyData
    // resolved would run one render too late: the leads query enables the moment companyLoading
    // flips false, so it would fire a full-collection fetch ordered by createdAt, then
    // immediately re-fire on the real sort. That is a wasted read of EVERY lead doc in the
    // company (getOrderedDocs takes the in-memory path for leads, so it fetches the whole
    // collection) plus a visible reorder flash on every page load.
    const [sortOverride, setSortOverride] = useState(() => {
        const column = searchParams.get('sort');
        return column ? { column, direction: searchParams.get('dir') || 'desc' } : null;
    });
    const [leadTypeFilter, setLeadTypeFilter] = useState(() => searchParams.get('filter') || null);
    const [addressSearch, setAddressSearch] = useState(() => searchParams.get('search') || '');
    const [debouncedSearch, setDebouncedSearch] = useState(() => searchParams.get('search') || '');
    const [selectedSources, setSelectedSources] = useState(() => {
        const sourcesParam = searchParams.get('sources');
        return sourcesParam ? sourcesParam.split('|').filter(s => s) : [];
    });
    const [availableSenders, setAvailableSenders] = useState([]);
    const [filterSenders, setFilterSenders] = useState(() => {
        try { const f = JSON.parse(searchParams.get('filters') || '{}'); return Array.isArray(f.senders) ? f.senders : []; } catch { return []; }
    });
    const [filterSenderSearch, setFilterSenderSearch] = useState('');
    // Portfolio facet: [{ id, name }] from the bootstrap. The filter stores
    // selected portfolio ids and displays names from this list.
    const [availablePortfolios, setAvailablePortfolios] = useState([]);
    const [automationIdFilter] = useState(() => searchParams.get('automationId') || null);
    const [archivedFilter, setArchivedFilter] = useState(() => searchParams.get('archived') === '1');
    const [archiveToast, setArchiveToast] = useState(null); // { leadId, address, timer }
    const [imageToast, setImageToast] = useState(null);
    const wasInWaitingRef = useRef(false);
    const [highlightFirstRow, setHighlightFirstRow] = useState(false);
    const [onboardingEmailBootstrapJob, setOnboardingEmailBootstrapJob] = useState(null);
    // Set when we stop waiting on a terminal onboarding job whose matches never
    // produced visible leads, so the overlay degrades to the waiting state
    // instead of spinning forever.
    const [bootstrapGaveUp, setBootstrapGaveUp] = useState(false);
    const bootstrapHealRef = useRef(false);
    const [processingToast, setProcessingToast] = useState(null); // { leadId, variant: 'processing'|'success'|'failed', message, propertyId? }
    // Separate slot for the createLeadManual failure toast — never share state
    // with processingToast, which is keyed to actual lead IDs whose enrichment
    // watchers may still be alive. Overwriting it here would silently drop a
    // different lead's in-flight processing/terminal toast on the floor.
    const [createLeadFailedToast, setCreateLeadFailedToast] = useState(null); // { message } | null
    const createLeadFailedToastTimerRef = useRef(null);
    const [actionsOpenForLead, setActionsOpenForLead] = useState(null); // Which lead's kebab menu is open
    const [createMenuOpen, setCreateMenuOpen] = useState(false);
    const navigate = useNavigate();
    const { companyId, companyData, loading: companyLoading, integrationsConnected, unipileAccounts, unipileAccountsLoading } = useCompany();
    const { user, userData, loading: userLoading } = useUser();
    const { showDarkBackground, setShowDarkBackground, sidebarCollapsed, setSidebarCollapsed } = useUI();
    const { options: dealStatusOptions } = useMemo(() => getDealStatuses(companyData), [companyData]);

    // The order the table is actually in: this visit's choice if there was one, else the
    // company's stored default, else newest-first. Derived during render so the very first
    // leads query carries it — see the note on `sortOverride`.
    //
    // The stored setting is validated as a PAIR, and discarded whole when the column is unmapped:
    // getFirestoreFieldPath silently falls back to createdAt for an unknown key (right for a
    // stale bookmark, wrong for a company setting), and validating the two halves separately
    // would let {column: 'typo', direction: 'asc'} quietly flip the table to oldest-first.
    const companyDefaultSort = isSortableColumn(companyData?.leadsDefaultSort?.column)
        ? companyData.leadsDefaultSort
        : null;
    const sortColumn = sortOverride ? sortOverride.column : (companyDefaultSort?.column ?? 'createdAt');
    const sortDirection = sortOverride
        ? sortOverride.direction
        : (companyDefaultSort?.direction === 'asc' ? 'asc' : 'desc');
    // Activity rail open/closed state — persists in localStorage. Default
    // open ≥1280px viewport, closed below. Used by the toggle button in
    // the page header + the rail component itself.
    const { open: railOpen, onToggle: toggleRail } = useActivityRailState();

    // Image viewer state machine for map-view LeadCard image clicks.
    // Mirrors the PropertyCard / RunCompsView pattern: clicking a card's
    // image opens ScrollableImages (grid); clicking an image there opens
    // ImageGallery (fullscreen swiper). Both rendered as overlays below.
    // The image list is captured at open time and held in local state so
    // the gallery doesn't depend on the lead's mutable shape.
    const {
        viewScrollableImages,
        viewImageGallery,
        imageViewProperty,
        imageViewPropertyIndex,
        showScrollableImages,
        closeScrollableImages,
        openImageGallery,
        closeImageGallery,
        setImageIndex
    } = useImageViewer();
    const [imageViewerImages, setImageViewerImages] = useState([]);
    // Room-tagged labels for the in-gallery section headers. Mirrors the
    // structure useLeadFetch builds for LeadDetailV2: { labels: [{firebaseUrl,
    // mlsPhotoUrl, roomType}], aduPass }. Lives on the property doc at
    // `data.subjectImageRoomLabels` (written by the image classifier — see
    // functions/underwritingV3/orchestrator.js + repairCostV2/
    // reanalyzeFromImages.js).
    //
    // Routed through cloudFunctionV2 + getLeadDetail (rather than a direct
    // Firestore SDK read) so the emulator→production fallback in
    // src/functions/api/index.js kicks in if the local emulator is down or
    // doesn't have the property doc. A direct getDoc() would just return
    // "not found" against the empty emulator and silently fail forever.
    // The per-propertyId cache avoids re-fetching when re-opening the same
    // gallery in one session.
    const [imageViewerLabels, setImageViewerLabels] = useState(null);
    const labelsCacheRef = useRef(new Map());
    const handleOpenImages = useCallback(async (lead, images) => {
        setImageViewerImages(images);
        setImageViewerLabels(null);
        showScrollableImages(lead);
        if (!lead.propertyId || !companyId) {
            console.warn('[LeadCard images] Skipping label fetch — missing propertyId or companyId', { propertyId: lead.propertyId, companyId });
            return;
        }
        const cached = labelsCacheRef.current.get(lead.propertyId);
        if (cached !== undefined) {
            if (cached) {
                setImageViewerLabels(cached);
                console.info(`[LeadCard images] Using cached labels for ${lead.propertyId}`);
            }
            return;
        }
        try {
            const url = process.env.REACT_APP_GET_LEAD_DETAIL;
            if (!url) {
                console.warn('[LeadCard images] REACT_APP_GET_LEAD_DETAIL not configured');
                return;
            }
            const res = await cloudFunctionV2(url, {
                companyId,
                propertyId: lead.propertyId
            });
            if (res.status !== 200) {
                console.warn('[LeadCard images] getLeadDetail failed', lead.propertyId, res.error || res.status);
                return;
            }
            const mirror = res.property?.subjectImageRoomLabels;
            if (mirror?.labels?.length) {
                const labels = { labels: mirror.labels, aduPass: !!mirror.aduPass };
                labelsCacheRef.current.set(lead.propertyId, labels);
                setImageViewerLabels(labels);
                console.info(`[LeadCard images] Loaded ${mirror.labels.length} room labels for ${lead.propertyId}`);
            } else {
                labelsCacheRef.current.set(lead.propertyId, null);
                console.info('[LeadCard images] Property has no subjectImageRoomLabels yet, gallery renders flat', lead.propertyId);
            }
        } catch (err) {
            console.warn('[LeadCard images] Failed to load room labels for', lead.propertyId, err);
        }
    }, [showScrollableImages, companyId]);

    // Auto-collapse the global side nav on this page — the leads workspace is
    // information-dense (toolbar + table + activity rail) and the side nav's
    // labels are the lowest-value pixels on screen. Run once on mount; restore
    // the prior state on unmount so other pages aren't affected. If the user
    // manually re-expands the side nav while on Leads, we don't fight them —
    // the effect only fires once.
    useEffect(() => {
        const wasExpanded = !sidebarCollapsed;
        if (wasExpanded) setSidebarCollapsed(true);
        return () => {
            if (wasExpanded) setSidebarCollapsed(false);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Column visibility state
    const [visibleColumns, setVisibleColumns] = useState(DEFAULT_VISIBLE_COLUMNS);

    // Column order state — per-filter map: { [filterKey]: [...columnKeys] }
    // e.g. { "all": [...], "direct_to_seller": [...], "mls_deal": [...] }
    const [columnOrders, setColumnOrders] = useState({});

    // Derive active column order from current lead type filter
    const filterKey = leadTypeFilter || 'all';
    const columnOrder = columnOrders[filterKey] || null;

    // Map view state
    const [tableView, setTableView] = useState(() => searchParams.get('view') !== 'map');
    // Map-mode selection: clicking a map marker highlights the corresponding
    // card in the side rail (rather than navigating away). Mirrors the
    // pattern used in leadDetailV2/RunCompsView.js for comp pins. Reset when
    // the leads dataset changes so a stale selection doesn't survive a
    // pagination / filter swap.
    //
    // Hover-driven sync between map and rail was removed by request — only
    // explicit clicks change selection. (Hover scroll/highlight created
    // accidental selection when the user just moused over the page.)
    const [selectedLeadId, setSelectedLeadId] = useState(null);

    const [rawMap, setRawMap] = useState(null);
    const [mapType, setMapType] = useState("roadmap");
    const [innerWidth, setInnerWidth] = useState(window.innerWidth);
    // Phone breakpoint — drives the SpencersDeals-style mobile chrome
    // (collapsed search, floating Map/Table toggle, full-screen map mode,
    // tap-a-pin bottom card). Matches SpencersDeals' isMobile threshold so the
    // two pages flip to their mobile layout at the same width.
    const isMobile = innerWidth <= 720;

    // Mobile collapses the search field to a magnifying-glass icon until tapped
    // — the table/map is the primary surface, so address search is a refinement.
    // The field also force-shows whenever a query is active (see showSearchField)
    // so an active search is never hidden behind the icon. searchInputRef focuses
    // the field on expand. Mirrors SpencersDeals.
    const [searchOpen, setSearchOpen] = useState(false);
    const searchInputRef = useRef(null);

    // Viewport filtering state
    const [viewportBounds, setViewportBounds] = useState(null);
    const [debouncedBounds, setDebouncedBounds] = useState(null);
    const [viewportFilterActive, setViewportFilterActive] = useState(() => searchParams.get('view') === 'map');

    // Restore last-used view from localStorage when no URL param is present.
    // Runs once when companyId resolves; URL param takes priority over saved pref.
    useEffect(() => {
        if (!companyId || searchParams.get('view')) return;
        try {
            const saved = localStorage.getItem(`leadsViewMode_${companyId}`);
            if (saved === 'map') {
                setTableView(false);
                setViewportFilterActive(true);
            }
        } catch { /* ignore — private window or storage full */ }
    }, [companyId]); // eslint-disable-line react-hooks/exhaustive-deps

    // Persist view preference whenever the user toggles it.
    useEffect(() => {
        if (!companyId) return;
        try {
            localStorage.setItem(`leadsViewMode_${companyId}`, tableView ? 'table' : 'map');
        } catch { /* ignore */ }
    }, [tableView, companyId]);

    // Available custom fields from company metadata
    const [availableCustomFields, setAvailableCustomFields] = useState([]);

    // Image URL modal state
    const [imageUrlModalOpen, setImageUrlModalOpen] = useState(false);
    const [imageUrlModalLeadId, setImageUrlModalLeadId] = useState(null);
    const [imageUrlModalValue, setImageUrlModalValue] = useState('');
    const [imageUrlModalLoading, setImageUrlModalLoading] = useState(false);

    // Identify Contact modal state
    const [contactModalOpen, setContactModalOpen] = useState(false);
    const [contactModalLeadId, setContactModalLeadId] = useState(null);
    const [contactModalRawData, setContactModalRawData] = useState(null);
    const [contactModalValues, setContactModalValues] = useState({ name: '', company: '', email: '', phone: '' });
    const [contactModalLoading, setContactModalLoading] = useState(false);
    const [companyContacts, setCompanyContacts] = useState([]);

    // "+ New lead" flow — single textarea. Stage 1 is the modal (textarea +
    // autocomplete + parse handoff). Stage 2 is a full-screen VerifyProperty
    // overlay, opened when createLeadVerifyProperty is non-null. Confirm in
    // Stage 2 calls createLeadManual with the parser-shaped payload — the same
    // path email/SMS/MCP leads take.
    const [createLeadModalOpen, setCreateLeadModalOpen] = useState(false);
    const [createLeadLoading, setCreateLeadLoading] = useState(false);
    const [createLeadLoadingMessage, setCreateLeadLoadingMessage] = useState(null);
    // Staged-progress checklist for paste flows (parse + short-circuit). The
    // address-tab pick path leaves this null and keeps using the single-line
    // loadingMessage row. Mutually exclusive with loadingMessage at render time.
    const [createLeadLoadingSteps, setCreateLeadLoadingSteps] = useState(null);
    const [createLeadError, setCreateLeadError] = useState(null);
    const [createLeadText, setCreateLeadText] = useState('');
    // initialProperty passed into VerifyProperty when Stage 2 opens (REAPI
    // propertyDetails result, optionally with parser-extracted overrides
    // layered on top of propertyInfo.bedrooms/etc).
    const [createLeadVerifyProperty, setCreateLeadVerifyProperty] = useState(null);
    // Parser output retained across Stage 1 → Stage 2 so that sender/notes/
    // price (which VerifyProperty doesn't capture) can be merged into the
    // createLeadManual payload on confirm.
    const [createLeadExtracted, setCreateLeadExtracted] = useState(null);
    // Preview-pending flag — when true, the parse-path's REAPI fetch has
    // resolved but we're holding the VerifyProperty overlay back so the user
    // can confirm the canonical address in a preview card first (Stage 1.5).
    // Pick-path (user explicitly clicked an autocomplete item) skips preview
    // because they already chose the address — no need to re-confirm.
    const [createLeadPreviewPending, setCreateLeadPreviewPending] = useState(false);
    // Modal input mode: 'address' (single-line + autocomplete) vs 'deal'
    // (textarea + paste-auto-submit). Defaults to 'address' as the
    // lower-commitment first impression — users with just an address don't
    // see a scary multi-line textarea, and users with a full deal switch to
    // the deal tab.
    const [createLeadMode, setCreateLeadMode] = useState('address');
    const createLeadAddress = useAddressAutocomplete({ minChars: 3 });
    // Destructure stable callbacks once so useCallback deps stay stable across
    // keystrokes — the hook returns a fresh object literal each render but
    // setQuery/reset themselves are memoized.
    const { setQuery: setCreateLeadAddressQuery, reset: resetCreateLeadAddress } = createLeadAddress;

    // Flow-token cancellation guard. Each open/close bumps the token; every
    // async handler captures the token at start and bails on any state
    // mutation if the token has changed. Without this, cancelling the modal
    // mid-parse lets the in-flight resolve and pop VerifyProperty open against
    // the user's intent.
    const createLeadFlowTokenRef = useRef(0);

    // Re-entrancy guard for the final "Create lead" submit. createLeadManual is
    // fired without awaiting from VerifyProperty and the overlay only closes
    // after it resolves, so without this a double-press would create duplicate
    // queued leads. A ref (not state) so back-to-back clicks in the same tick
    // are caught synchronously.
    const createLeadSubmittingRef = useRef(false);

    // Edit Address modal state
    const [editAddressModalOpen, setEditAddressModalOpen] = useState(false);
    const [editAddressLeadId, setEditAddressLeadId] = useState(null);
    const [editAddressCurrentAddress, setEditAddressCurrentAddress] = useState(null);
    const editAddressAutocomplete = useAddressAutocomplete({ minChars: 3 });
    const { setQuery: setEditAddressQuery, reset: resetEditAddress } = editAddressAutocomplete;
    const [editAddressSelected, setEditAddressSelected] = useState(false);
    const [editAddressSelectedLabel, setEditAddressSelectedLabel] = useState('');
    const [editAddressStreet, setEditAddressStreet] = useState('');
    const [editAddressCity, setEditAddressCity] = useState('');
    const [editAddressState, setEditAddressState] = useState('');
    const [editAddressZipcode, setEditAddressZipcode] = useState('');
    const [editAddressLoading, setEditAddressLoading] = useState(false);
    const [editAddressError, setEditAddressError] = useState(null);

    // Portfolio modal state
    const [portfolioModalOpen, setPortfolioModalOpen] = useState(false);
    const [portfolioLoading, setPortfolioLoading] = useState(false);
    const [portfolioError, setPortfolioError] = useState(null);
    const [portfolioName, setPortfolioName] = useState('');
    const [portfolioCombinedPrice, setPortfolioCombinedPrice] = useState('');
    const [portfolioSelectedLeadIds, setPortfolioSelectedLeadIds] = useState([]);
    const [portfolioSelectedLeadsData, setPortfolioSelectedLeadsData] = useState({});
    const [portfolioSearchTerm, setPortfolioSearchTerm] = useState('');
    const [debouncedPortfolioSearch, setDebouncedPortfolioSearch] = useState('');
    const [portfolioSearchResults, setPortfolioSearchResults] = useState(null);
    const [portfolioSearchLoading, setPortfolioSearchLoading] = useState(false);

    // Filter modal state
    const [filterModalOpen, setFilterModalOpen] = useState(false);
    const [filterPriceMin, setFilterPriceMin] = useState('');
    const [filterPriceMax, setFilterPriceMax] = useState('');
    const [filterStateName, setFilterStateName] = useState('');
    const [filterCity, setFilterCity] = useState('');
    const [filterBedsMin, setFilterBedsMin] = useState('');
    const [filterBedsMax, setFilterBedsMax] = useState('');
    const [filterBathsMin, setFilterBathsMin] = useState('');
    const [filterBathsMax, setFilterBathsMax] = useState('');
    const [filterSqftMin, setFilterSqftMin] = useState('');
    const [filterSqftMax, setFilterSqftMax] = useState('');
    const [filterYearBuiltMin, setFilterYearBuiltMin] = useState('');
    const [filterYearBuiltMax, setFilterYearBuiltMax] = useState('');
    const [filterSpreadMin, setFilterSpreadMin] = useState('');
    const [filterMaoVsAskingMax, setFilterMaoVsAskingMax] = useState('');
    // Co-living financing filters. `financingPaths` narrows to how the deal would be bought
    // (hard money → refi, DSCR purchase, or either); `dealScoreMin` is the floor on the lead's
    // deal score (0-100, higher is better) — a bridge-only number, so its input is gated on the
    // bridge path existing.
    const [filterFinancingPaths, setFilterFinancingPaths] = useState(() => {
        try { return resolveFinancingPathFilter(JSON.parse(searchParams.get('filters') || '{}')); } catch { return []; }
    });
    const [filterDealScoreMin, setFilterDealScoreMin] = useState('');
    const [filterDateMin, setFilterDateMin] = useState('');
    const [filterDateMax, setFilterDateMax] = useState('');
    // Draft for the archived toggle inside FilterModal. The live `archivedFilter`
    // state above is what actually drives the query; this is just the modal-local
    // copy that flips on Apply (or rolls back on Close).
    const [filterArchived, setFilterArchived] = useState(false);
    const [appliedFilters, setAppliedFilters] = useState(() => {
        try { return JSON.parse(searchParams.get('filters') || '{}'); } catch { return {}; }
    });
    const [availableStates, setAvailableStates] = useState([]);
    const [availableCities, setAvailableCities] = useState([]);
    const [availablePropertyTypes, setAvailablePropertyTypes] = useState([]);
    const [availableLeadTypes, setAvailableLeadTypes] = useState(null);
    const [filterPropertyType, setFilterPropertyType] = useState(() => {
        try { const f = JSON.parse(searchParams.get('filters') || '{}'); return Array.isArray(f.propertyType) ? f.propertyType : []; } catch { return []; }
    });
    const [filterAssignees, setFilterAssignees] = useState(() => {
        try { const f = JSON.parse(searchParams.get('filters') || '{}'); return Array.isArray(f.assignees) ? f.assignees : []; } catch { return []; }
    });
    const [filterDealStatuses, setFilterDealStatuses] = useState(() => {
        try { const f = JSON.parse(searchParams.get('filters') || '{}'); return Array.isArray(f.dealStatuses) ? f.dealStatuses : []; } catch { return []; }
    });
    const [filterPortfolios, setFilterPortfolios] = useState(() => {
        try { const f = JSON.parse(searchParams.get('filters') || '{}'); return Array.isArray(f.portfolios) ? f.portfolios : []; } catch { return []; }
    });
    // Auction (foreclosure / trustee-sale) filters — Tier 1.
    const [filterAuctionStatus, setFilterAuctionStatus] = useState(() => {
        try { const f = JSON.parse(searchParams.get('filters') || '{}'); return Array.isArray(f.auctionStatus) ? f.auctionStatus : []; } catch { return []; }
    });
    const [filterLienPosition, setFilterLienPosition] = useState(() => {
        try { const f = JSON.parse(searchParams.get('filters') || '{}'); return Array.isArray(f.lienPosition) ? f.lienPosition : []; } catch { return []; }
    });
    const [filterAuctionDateMin, setFilterAuctionDateMin] = useState('');
    const [filterAuctionDateMax, setFilterAuctionDateMax] = useState('');

    // Active filter count for badge
    const activeFilterCount = useMemo(() => {
        return Object.values(appliedFilters).filter(v =>
            Array.isArray(v) ? v.length > 0 : (v !== '' && v != null)
        ).length;
    }, [appliedFilters]);

    // Auction leads only exist for some companies. Surface the Auction filter
    // section whenever the COMPANY has auction leads — primarily from the
    // whole-DB server facet (leadsBootstrap filterOptions.auctionStatuses, the
    // same mechanism as states/senders), unioned with statuses on the currently
    // loaded page as a resilient fallback (covers the window before the bootstrap
    // resolves / an older deployed function without the facet). An already-applied
    // auction filter keeps the section visible via the modal's own gate.
    const [auctionStatusFacet, setAuctionStatusFacet] = useState([]);
    const availableAuctionStatuses = useMemo(() => {
        const set = new Set(auctionStatusFacet);
        (leads || []).forEach((l) => { if (l?.auction?.status) set.add(l.auction.status); });
        return Array.from(set);
    }, [auctionStatusFacet, leads]);

    // Which financing paths this company's deals ACTUALLY use — the same two-source pattern as
    // auction statuses above: the whole-DB server facet (leadsBootstrap filterOptions
    // .financingPaths) unioned with the paths on the currently loaded page, which covers the
    // window before the bootstrap resolves and an older deployed function without the facet.
    //
    // This is what answers "only show the filter when deals exist with it": no bridgeRefi leads,
    // no bridgeRefi chip; neither path (i.e. no co-living at all), no section.
    const [financingPathFacet, setFinancingPathFacet] = useState([]);
    const availableFinancingPaths = useMemo(() => {
        const set = new Set(financingPathFacet);
        (leads || []).forEach((l) => { if (l?.coLivingFinancingPath) set.add(l.coLivingFinancingPath); });
        // Ordered by the canonical option list rather than by discovery, so the chips don't
        // reshuffle between the fallback read and the facet landing.
        return FINANCING_PATH_OPTIONS.filter((o) => set.has(o.value)).map((o) => o.value);
    }, [financingPathFacet, leads]);

    // Helper to update search params while preserving existing ones
    // Pass null to remove a param
    const updateSearchParams = useCallback((updates) => {
        setSearchParams(prev => {
            const newParams = new URLSearchParams(prev);
            Object.entries(updates).forEach(([key, value]) => {
                if (value === null || value === undefined || value === '') {
                    newParams.delete(key);
                } else {
                    newParams.set(key, value);
                }
            });
            return newParams;
        });
    }, [setSearchParams]);

    // Stable ref for updateSearchParams (avoids React Router's unstable setSearchParams
    // triggering effects on every URL change)
    const updateSearchParamsRef = useRef(updateSearchParams);
    updateSearchParamsRef.current = updateSearchParams;

    // Sync dark background with modal visibility. The "+ New lead" Stage 2
    // VerifyProperty overlay is opaque and sits above the dark backdrop;
    // gate the backdrop off when the overlay is up so we're not paying for
    // an invisible darkening layer underneath. Preview state (Stage 1.5)
    // keeps the modal up, so the backdrop must stay on.
    useEffect(() => {
        const createLeadModalVisible = createLeadModalOpen
            && (createLeadVerifyProperty === null || createLeadPreviewPending);
        setShowDarkBackground(imageUrlModalOpen || createLeadModalVisible || portfolioModalOpen || filterModalOpen || editAddressModalOpen || contactModalOpen);
        return () => setShowDarkBackground(false);
    }, [imageUrlModalOpen, createLeadModalOpen, createLeadVerifyProperty, createLeadPreviewPending, portfolioModalOpen, filterModalOpen, editAddressModalOpen, contactModalOpen, setShowDarkBackground]);

    // The filters modal is a full-height bottom sheet; on mobile the floating
    // bottom nav + setup checklist widget (both portaled/fixed siblings of the
    // outlet, so the modal can't cover them) poke over its Clear/Apply footer.
    // Flag the open state on <body> so their CSS can hide them for the modal's
    // lifetime. Scoped to this modal — other dark-background modals are
    // unaffected.
    useEffect(() => {
        document.body.classList.toggle('leads-filters-modal-open', filterModalOpen);
        return () => document.body.classList.remove('leads-filters-modal-open');
    }, [filterModalOpen]);

    // The Leads mobile view floats a centered view-toggle FAB at the bottom. The
    // app-wide setup-checklist widget (a fixed sibling of the outlet, Layout.js)
    // would sit in the same band and collide with it. Flag the FAB's presence on
    // <body> so the widget's CSS can lift itself above the FAB — scoped to this
    // page so the pill keeps its normal position on every other mobile page.
    useEffect(() => {
        document.body.classList.toggle('leads-has-view-toggle-fab', isMobile);
        return () => document.body.classList.remove('leads-has-view-toggle-fab');
    }, [isMobile]);

    // Clean up archive toast timer on unmount
    useEffect(() => {
        return () => {
            if (archiveToast?.timer) clearTimeout(archiveToast.timer);
        };
    }, [archiveToast]);

    // Column prefs and custom-field metadata are loaded by the leadsBootstrap
    // hook (with localStorage as a same-frame offline fallback for column prefs).

    // Debounce search input - wait 300ms after user stops typing
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(addressSearch);
        }, 300);
        return () => clearTimeout(timer);
    }, [addressSearch]);

    // Sync debounced search to URL (skip when value hasn't actually changed — StrictMode-safe)
    const prevDebouncedSearchRef = useRef(debouncedSearch);
    useEffect(() => {
        if (prevDebouncedSearchRef.current === debouncedSearch) {
            return;
        }
        prevDebouncedSearchRef.current = debouncedSearch;
        updateSearchParamsRef.current({ search: debouncedSearch || null, page: '1' });
    }, [debouncedSearch]);

    // Debounce viewport bounds changes - wait 500ms after user stops panning/zooming
    useEffect(() => {
        if (!viewportBounds) {
            setDebouncedBounds(null);
            return;
        }
        const timer = setTimeout(() => {
            setDebouncedBounds(viewportBounds);
        }, 500);
        return () => clearTimeout(timer);
    }, [viewportBounds]);

    // (Removed) Used to reset table-view `?page=1` whenever viewport bounds
    // changed in map view. Now that map view has no pagination and the two
    // queries are independent, panning the map should not mutate table state.

    // Debounce portfolio search input - 300ms
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedPortfolioSearch(portfolioSearchTerm);
        }, 300);
        return () => clearTimeout(timer);
    }, [portfolioSearchTerm]);

    // Query leads when portfolio search changes
    useEffect(() => {
        if (!debouncedPortfolioSearch || !companyId || !portfolioModalOpen) {
            setPortfolioSearchResults(null);
            setPortfolioSearchLoading(false);
            return;
        }

        let cancelled = false;
        const searchLeads = async () => {
            setPortfolioSearchLoading(true);
            try {
                const url = process.env.REACT_APP_GET_ORDERED_DOCS;
                const queryData = await cloudFunctionV2(url, {
                    collection: "companies",
                    subcollection: "leads",
                    document: companyId,
                    field: 'data.createdAt',
                    order: 'desc',
                    limit: 50,
                    page: 1,
                    searchField: 'data.address.streetAddressLower',
                    searchValue: debouncedPortfolioSearch.toLowerCase()
                });
                if (!cancelled) {
                    if (queryData.status === 200) {
                        const results = [];
                        queryData.data.forEach(d => results.push(d.data));
                        setPortfolioSearchResults(results);
                    } else {
                        setPortfolioSearchResults([]);
                    }
                }
            } catch (err) {
                console.error('Portfolio lead search error:', err);
                if (!cancelled) setPortfolioSearchResults([]);
            } finally {
                if (!cancelled) setPortfolioSearchLoading(false);
            }
        };

        searchLeads();
        return () => { cancelled = true; };
    }, [debouncedPortfolioSearch, companyId, portfolioModalOpen]);

    // Track window width for responsive behavior
    useEffect(() => {
        const handleResize = () => setInnerWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // When the mobile search expands, drop focus straight into the field so the
    // keyboard comes up with it (no second tap). Mirrors SpencersDeals.
    useEffect(() => {
        if (searchOpen) searchInputRef.current?.focus();
    }, [searchOpen]);

    // Filter leads that have valid coordinates for map view
    const mappableLeads = useMemo(() => {
        return leads.filter(lead =>
            lead.latitude != null && lead.longitude != null
        );
    }, [leads]);

    // Promote the map-selected lead to the top of the rail. Keeps relative
    // ordering of the rest intact so the user can still see "the rest of the
    // page" in its original order. Same idea as RunCompsView's clicked-comp
    // pinning to index 0.
    const orderedMappableLeads = useMemo(() => {
        if (!selectedLeadId) return mappableLeads;
        const selected = mappableLeads.find((l) => l.leadId === selectedLeadId);
        if (!selected) return mappableLeads;
        return [selected, ...mappableLeads.filter((l) => l.leadId !== selectedLeadId)];
    }, [mappableLeads, selectedLeadId]);

    // When a new selection lands, scroll the cards rail back to the top so
    // the freshly-promoted card is visible. setTimeout gives React a frame
    // to commit the reordered DOM before we measure scroll position.
    const cardsScrollRef = useRef(null);
    useEffect(() => {
        if (!selectedLeadId || !cardsScrollRef.current) return undefined;
        const id = setTimeout(() => {
            cardsScrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
        }, 50);
        return () => clearTimeout(id);
    }, [selectedLeadId]);

    // Virtualize the map-view card rail (the iOS-OOM structural fix, same as
    // SpencersDeals). Only the ~10-15 cards in/near the viewport mount; the rest
    // unmount so Safari reclaims their decoded-image bitmaps. Unlike SpencersDeals,
    // .leads-cards-wrapper is an ELEMENT scroll on BOTH breakpoints (desktop
    // overflow-y:auto; mobile max-height:400px), so a single useVirtualizer on
    // cardsScrollRef serves both — no window virtualizer / scrollMargin needed.
    // Seed row height per breakpoint (LeadCard flips horizontal→vertical at 720px);
    // measureElement corrects to the real card. The 12px the old flex `gap` gave
    // between cards now lives in .leads-rail-vrow padding-bottom (getBoundingClientRect
    // includes it). The carousel itself is already bounded by CustomSwiper's
    // active±1 slide windowing (shared component), so this just bounds card count.
    const railVirtualizer = useVirtualizer({
        count: orderedMappableLeads.length,
        getScrollElement: () => cardsScrollRef.current,
        estimateSize: () => (innerWidth <= 720 ? 384 : 132),
        overscan: 4,
        getItemKey: (i) => orderedMappableLeads[i]?.leadId ?? i,
        measureElement: (el) => el?.getBoundingClientRect().height || (innerWidth <= 720 ? 384 : 132),
    });
    // Re-measure when the set reorders/swaps or the breakpoint (and thus the
    // card layout + estimate) changes.
    useEffect(() => { railVirtualizer.measure(); }, [orderedMappableLeads, innerWidth <= 720]); // eslint-disable-line react-hooks/exhaustive-deps

    // Clear selection whenever the underlying leads set swaps (page change,
    // filter, search) so a ghost selection doesn't carry over.
    useEffect(() => {
        setSelectedLeadId(null);
    }, [leads]);

    // Card click — full navigation to LeadDetail (map rail + mobile cards share
    // this; the desktop table has its own row onClick in LeadsTable). Gate on
    // isLeadWorking so a still-processing / mid-underwriting lead isn't openable
    // here either — the same rule LeadsTable applies to its rows.
    const handleLeadClick = useCallback((lead) => {
        if (lead.propertyId && !isLeadWorking(lead)) {
            recordEvent('lead_opened', { propertyId: lead.propertyId, leadType: lead.leadType || null });
            navigate(`/leads/${lead.propertyId}`, {
                state: { leadsSearch: window.location.search }
            });
        }
    }, [navigate]);

    // Map marker click — DON'T navigate. Instead promote the clicked lead's
    // card to the top of the rail and flag it as selected so the user can
    // glance at the card detail before deciding to open it. Clicking the
    // same marker again deselects (matches RunCompsView.js:127 viewComp).
    const handleMapPinClick = useCallback((lead) => {
        setSelectedLeadId((prev) => (prev === lead.leadId ? null : lead.leadId));
    }, []);

    // Save preferences to Firebase and localStorage
    const saveColumnPreferences = async (newVisibleColumns, newColumnOrders) => {
        if (!companyId) return;

        const prefs = {
            visibleColumns: newVisibleColumns,
            columnOrders: newColumnOrders,
            lastUpdated: new Date().toISOString()
        };

        // Save to localStorage immediately (for fast access)
        localStorage.setItem(
            `leads_column_preferences_${companyId}`,
            JSON.stringify(prefs)
        );

        // Save to Firebase (for cross-device sync)
        if (user?.userId) {
            try {
                await setSubData('companies', companyId, 'user_preferences', user.userId, {
                    leadsColumns: prefs
                });
            } catch (error) {
                console.error('Error saving column preferences to Firebase:', error);
            }
        }
    };

    // Handle column toggle
    const handleColumnToggle = (columnKey) => {
        setVisibleColumns(prev => {
            const newColumns = prev.includes(columnKey)
                ? prev.filter(k => k !== columnKey)
                : [...prev, columnKey];

            saveColumnPreferences(newColumns, columnOrders);
            return newColumns;
        });
    };

    // Handle column reorder (drag and drop) — saves per current lead type filter
    const handleColumnReorder = (activeId, overId) => {
        // Get all column keys (standard + custom)
        const allColumnKeys = [
            ...STANDARD_COLUMNS.map(c => c.key),
            ...availableCustomFields.map(f => `custom_${f.key}`)
        ];

        // Build current order, ensuring all columns are included
        let currentOrder;
        if (columnOrder) {
            const validSavedOrder = columnOrder.filter(key => allColumnKeys.includes(key));
            const missingColumns = allColumnKeys.filter(key => !validSavedOrder.includes(key));
            currentOrder = [...validSavedOrder, ...missingColumns];
        } else {
            currentOrder = allColumnKeys;
        }

        const oldIndex = currentOrder.indexOf(activeId);
        const newIndex = currentOrder.indexOf(overId);

        if (oldIndex === -1 || newIndex === -1) return;

        const newOrder = arrayMove(currentOrder, oldIndex, newIndex);
        const newColumnOrders = { ...columnOrders, [filterKey]: newOrder };
        setColumnOrders(newColumnOrders);
        saveColumnPreferences(visibleColumns, newColumnOrders);
    };

    // Build column config for ColumnToggle component
    const buildColumnConfig = () => {
        const standardConfig = STANDARD_COLUMNS.map(col => ({
                key: col.key,
                label: col.label,
                visible: visibleColumns.includes(col.key),
                isCustom: false
            }));

        const customConfig = availableCustomFields.map(field => ({
            key: `custom_${field.key}`,
            label: field.displayName,
            visible: visibleColumns.includes(`custom_${field.key}`),
            isCustom: true
        }));

        const allConfig = [...standardConfig, ...customConfig];

        // Apply column order if set
        if (columnOrder) {
            return columnOrder
                .map(key => allConfig.find(col => col.key === key))
                .filter(Boolean)
                .concat(allConfig.filter(col => !columnOrder.includes(col.key)));
        }

        return allConfig;
    };

    // Get valid page from search params
    const getValidPage = (val) => {
        const num = parseInt(val, 10);
        return Number.isNaN(num) || num < 1 ? 1 : num;
    };
    const page = getValidPage(searchParams.get("page"));

    // -------------------------------------------------------------------
    // Data layer (TanStack Query)
    //
    // Bootstrap: ONE round-trip on first paint that returns filter options,
    // custom-field metadata and saved column prefs. Cached for 5 min per
    // (company,user) so filter toggles don't refire it.
    //
    // Page query: drives the leads array. Re-fires only when leadsParams
    // change. Stays paused (`enabled: false`) on initial load until the
    // bootstrap finishes — that lets the bootstrap's column prefs pick the
    // right `fields` projection so we don't fetch columns the user has
    // hidden. After the first paint both hooks coexist; bootstrap stays
    // cached, page query handles all subsequent fetches.
    // -------------------------------------------------------------------
    const queryClient = useQueryClient();

    // (?sinceDays= and ?dedupOnly= URL params were removed when Home was
    // merged into Leads — they only existed for Home Activity deep links.
    // The activity rail now owns recent-events visibility; if a similar
    // affordance is wanted later, expose date-range and dedup as first-class
    // filters in LeadsFilterModal rather than re-introducing URL-only signals.)

    // Table-view query: sortable/paginated full list with all the filter
    // dimensions getOrderedDocs supports. Bounds NEVER apply here — map and
    // table now use two specialized endpoints (see useLeadsInBounds below).
    const leadsParams = useMemo(() => buildLeadsParams({
        sortColumn,
        sortDirection,
        page,
        leadTypeFilter,
        debouncedSearch,
        selectedSources,
        automationIdFilter,
        bounds: null,
        archivedFilter,
        propertyFilters: appliedFilters
    }), [sortColumn, sortDirection, page, leadTypeFilter, debouncedSearch, selectedSources, automationIdFilter, archivedFilter, appliedFilters]);

    const bootstrapQuery = useLeadsBootstrap({
        companyId,
        userId: user?.userId,
        enabled: !!companyId && !companyLoading
    });

    // Runs in BOTH table and map views: the table needs the page directly,
    // and the map reads its top-of-table leads to pick an initial center
    // that honors the active filters (the bounds query can't do that — it
    // needs bounds, which we're trying to derive).
    const leadsPageQuery = useLeadsPage({
        companyId,
        leadsParams,
        enabled: !!companyId && !companyLoading
    });

    // True first-run empty: fetch settled, no leads, and no filter / search
    // narrowing the result. Uses leadsPageQuery.data.total (not the viewport-
    // bounded `leads` state) so map view with zero leads also triggers this —
    // the bounds query reflects what's in the viewport, not whether leads exist.
    const isFirstRunEmpty = !!companyId
        && leadsPageQuery.isSuccess
        && (leadsPageQuery.data?.total ?? 0) === 0
        && !archivedFilter
        && !debouncedSearch
        && !leadTypeFilter
        && !automationIdFilter
        && activeFilterCount === 0
        && selectedSources.length === 0;

    const bootstrapStatus = onboardingEmailBootstrapJob?.status;
    const bootstrapMatchCount = onboardingEmailBootstrapJob?.matchCount ?? (onboardingEmailBootstrapJob?.matches?.length || 0);
    const bootstrapCreatedCount = onboardingEmailBootstrapJob?.createdLeadIds?.length || 0;
    const bootstrapSearching = isFirstRunEmpty && (bootstrapStatus === 'queued' || bootstrapStatus === 'searching' || bootstrapStatus === 'discovering');
    // Only keep the "processing" overlay up while a lead is genuinely expected.
    // 'complete'/'pool_ready' are terminal: treat them as processing ONLY when
    // there are buy-box matches that haven't produced any leads yet (created via
    // the self-heal trigger + surfaced via the polling fallback below). Once the
    // job has already created leads once (bootstrapCreatedCount > 0), it has done
    // its job — if the board reads empty now it's because those leads were since
    // archived/removed, not because anything is still processing, so this must
    // NOT re-trigger the overlay on a later page load. A terminal job with zero
    // matches — or one we've given up waiting on — also falls through to the
    // normal waiting/empty state instead of spinning forever.
    const bootstrapProcessing = isFirstRunEmpty && !bootstrapGaveUp && (
        bootstrapStatus === 'processing' ||
        bootstrapStatus === 'found' ||
        ((bootstrapStatus === 'pool_ready' || bootstrapStatus === 'complete') && bootstrapMatchCount > 0 && bootstrapCreatedCount === 0)
    );
    const showBootstrapState = bootstrapSearching || bootstrapProcessing;
    const showWaitingState = isFirstRunEmpty && integrationsConnected && !showBootstrapState;
    const showProcessingState = showBootstrapState;
    const bootstrapProcessingMessage = bootstrapSearching
        ? 'Looking for your latest single-family deal email...'
        : onboardingEmailBootstrapJob?.selectedCandidateType === 'non_sfh_fallback'
            ? 'We found a recent deal. Your first analysis is processing...'
            : 'We found a likely single-family deal. Your first analysis is processing...';
    const bootstrapNoneMessage = bootstrapStatus === 'none'
        ? 'No recent qualifying deal emails found in the last 30 days.'
        : null;

    // Initial map framing: a bounding box around the top-of-table (page-1)
    // leads matching active filters, so opening the map frames where the
    // recent deals actually are and shows the most pins — rather than parking
    // at a default center. Read directly from leadsPageQuery (not the `leads`
    // state, which gets swapped to bounds-query results in map mode) so the
    // framing reflects "top of the eligible list", independent of pan.
    //
    // Outliers are trimmed to the 10th–90th percentile per axis so a single
    // stray lead in another state can't blow the box out to the whole country
    // (the same robustness the previous median-center approach gave us).
    // Returns null when no page-1 leads have coords, in which case LeadsMap
    // keeps its default view.
    const initialBounds = useMemo(() => {
        const docs = leadsPageQuery.data?.data;
        if (!docs || docs.length === 0) return null;
        const lats = [];
        const lngs = [];
        for (const doc of docs) {
            const lat = doc?.data?.latitude;
            const lng = doc?.data?.longitude;
            if (typeof lat === 'number' && typeof lng === 'number' && !isNaN(lat) && !isNaN(lng)) {
                lats.push(lat);
                lngs.push(lng);
            }
        }
        if (lats.length === 0) return null;
        lats.sort((a, b) => a - b);
        lngs.sort((a, b) => a - b);
        // Percentile pick that clamps to valid indices; with <5 points this
        // collapses toward the full min/max, which is fine for a small set.
        const pct = (arr, p) => arr[Math.min(arr.length - 1, Math.max(0, Math.round((arr.length - 1) * p)))];
        return {
            south: pct(lats, 0.10),
            north: pct(lats, 0.90),
            west: pct(lngs, 0.10),
            east: pct(lngs, 0.90),
            count: lats.length
        };
    }, [leadsPageQuery.data]);

    // Map-view query: geohash-indexed, capped at MAP_VIEWPORT_LIMIT, no
    // pagination. Re-runs automatically whenever debouncedBounds OR any
    // filter dimension changes (map idle → debounce → key changes → fetch).
    // Shares leadsParams with the table query so the same filter UI applies
    // to both — Airbnb's map view respects the active filters and so do we.
    const leadsInBoundsQuery = useLeadsInBounds({
        companyId,
        bounds: debouncedBounds,
        leadsParams,
        enabled: !!companyId && !companyLoading && !tableView
    });

    // Sync whichever query is active into the shared local state. Switching
    // views swaps which one drives `leads` / `leadsTotal`.
    useEffect(() => {
        if (tableView) {
            if (!leadsPageQuery.data) return;
            const newLeads = (leadsPageQuery.data.data || []).map(d => d.data);
            setLeads(newLeads);
            setLeadsTotal(leadsPageQuery.data.total || 0);
        } else {
            if (!leadsInBoundsQuery.data) return;
            const newLeads = (leadsInBoundsQuery.data.data || []).map(d => d.data);
            setLeads(newLeads);
            // For map view, "total" means "leads in current viewport" (pre-cap).
            // The rail header reads it via leadsInBoundsQuery.data.totalInBounds.
            setLeadsTotal(leadsInBoundsQuery.data.totalInBounds || 0);
        }
    }, [tableView, leadsPageQuery.data, leadsInBoundsQuery.data]);

    // Live in-flight leads (status queued/processing) via Firestore onSnapshot
    // — the SAME source that drives the "Processing N new lead" header pill and
    // the rail's "Processing now" section. getOrderedDocs is heavy + un-polled
    // and the just-created placeholder usually isn't in it yet, so a freshly
    // created lead would otherwise be invisible in the table for ~30-60s. We
    // pin these on top of the table so the user gets immediate confirmation.
    const { leads: liveProcessingLeads } = useProcessingLeads(companyId);

    // Table data = live processing leads pinned on top + the canonical page,
    // deduped by leadId so a processing lead that getOrderedDocs HAS returned
    // doesn't appear twice. Pinned regardless of search/filter/sort (product
    // decision) so the user always sees their in-flight lead. Scoped to the
    // table view (map needs lat/lng the placeholder lacks) and skipped in the
    // archived view (in-flight leads are never archived). Derived — never
    // mutates `leads` state, so the setLeads sync + pendingLeadKey watcher stay
    // clean. When a lead completes it drops from the snapshot and the existing
    // watchers refetch getOrderedDocs, handing the row back to its sorted spot.
    const tableData = useMemo(() => {
        if (!tableView || archivedFilter || liveProcessingLeads.length === 0) {
            return leads;
        }
        const processingRows = liveProcessingLeads.map(l => l.data);
        const processingIds = new Set(liveProcessingLeads.map(l => l.leadId));
        const rest = leads.filter(l => !processingIds.has(l.leadId));
        return [...processingRows, ...rest];
    }, [tableView, archivedFilter, liveProcessingLeads, leads]);

    // Mobile table view renders the same LeadCards as a single full-width column
    // (the SpencersDeals list pattern) instead of the desktop table — phones can't
    // show a multi-column grid. This list replaces the whole content area, so it
    // rides the WINDOW scroll (unlike the map rail's element scroll); a window
    // virtualizer keeps decoded-image memory bounded the same way the rail does.
    const tableCardsListRef = useRef(null);
    const tableCardsActive = isMobile && tableView;
    // Window-scroll virtualization needs the list's document-top offset; each
    // row's transform subtracts it. The toolbar above the list reflows on its own
    // (search expands, filter chips wrap, the type tabs show/hide) without the row
    // count changing, so a fixed dep list goes stale and the cards drift. A
    // ResizeObserver on <body> recomputes the offset on ANY layout shift above the
    // list. Setting the margin only moves absolutely-positioned rows inside a
    // fixed-height spacer, so it can't change body height — no observer loop.
    const [tableCardsScrollMargin, setTableCardsScrollMargin] = useState(0);
    useLayoutEffect(() => {
        const el = tableCardsListRef.current;
        if (!tableCardsActive || !el) { setTableCardsScrollMargin(0); return undefined; }
        const measure = () => setTableCardsScrollMargin(Math.round(el.getBoundingClientRect().top + window.scrollY));
        measure();
        const ro = new ResizeObserver(measure);
        ro.observe(document.body);
        return () => ro.disconnect();
    }, [tableCardsActive, tableData.length]);
    const tableCardsVirtualizer = useWindowVirtualizer({
        // Only attach window scroll/resize listeners when the mobile card list is
        // actually on screen — otherwise the virtualizer re-renders the whole page
        // on every desktop window scroll for a list that's never rendered.
        enabled: tableCardsActive,
        count: tableData.length,
        estimateSize: () => 384,
        overscan: 4,
        getItemKey: (i) => tableData[i]?.leadId ?? i,
        scrollMargin: tableCardsScrollMargin,
        measureElement: (el) => el?.getBoundingClientRect().height || 384,
    });
    useEffect(() => {
        if (tableCardsActive) tableCardsVirtualizer.measure();
    }, [tableData, tableCardsActive]); // eslint-disable-line react-hooks/exhaustive-deps

    // Show loading only on the initial fetch for a given key, not on
    // background refetches — placeholderData covers UX during refetch.
    const tableLoading = leadsPageQuery.isLoading || (leadsPageQuery.isFetching && !leadsPageQuery.data);
    const mapLoading = leadsInBoundsQuery.isLoading || (leadsInBoundsQuery.isFetching && !leadsInBoundsQuery.data);
    // Pure derived value — never a piece of state mutated from elsewhere.
    // A prior version set this to `true` in onPageChange and relied on an
    // effect to reset it; on a prefetched paginate the underlying query state
    // never toggled, so the effect never re-fired and the spinner hung forever.
    const leadsLoading = tableView ? tableLoading : mapLoading;

    // Refetch signal: true while a background fetch is in-flight (prior data
    // still on screen via placeholderData). Drives the thin progress bar at
    // the top of the table/rail + a dimmed state on the view toggle.
    const isRefetching = tableView
        ? (leadsPageQuery.isFetching && !leadsPageQuery.isLoading)
        : (leadsInBoundsQuery.isFetching && !leadsInBoundsQuery.isLoading);

    // Map-view cap signaling for the rail header.
    const mapTotalInBounds = leadsInBoundsQuery.data?.totalInBounds ?? 0;
    const mapCapped = leadsInBoundsQuery.data?.capped === true;

    // Sync bootstrap metadata into the existing useState so filter dropdowns
    // and the column toggle still consume from local state. This effect
    // fires once when bootstrap resolves (and again only if the bootstrap
    // cache is invalidated, which happens on company change).
    const bootstrapAppliedRef = useRef(false);
    useEffect(() => {
        const data = bootstrapQuery.data;
        if (!data) return;

        if (Array.isArray(data.filterOptions?.senders)) setAvailableSenders(data.filterOptions.senders);
        if (Array.isArray(data.filterOptions?.states)) setAvailableStates(data.filterOptions.states);
        if (Array.isArray(data.filterOptions?.cities)) setAvailableCities(data.filterOptions.cities);
        if (Array.isArray(data.filterOptions?.propertyTypes)) setAvailablePropertyTypes(data.filterOptions.propertyTypes);
        if (Array.isArray(data.filterOptions?.leadTypes)) setAvailableLeadTypes(data.filterOptions.leadTypes);
        if (Array.isArray(data.filterOptions?.auctionStatuses)) setAuctionStatusFacet(data.filterOptions.auctionStatuses);
        if (Array.isArray(data.filterOptions?.financingPaths)) setFinancingPathFacet(data.filterOptions.financingPaths);
        if (Array.isArray(data.filterOptions?.portfolios)) setAvailablePortfolios(data.filterOptions.portfolios);
        if (Array.isArray(data.customFields)) setAvailableCustomFields(data.customFields);

        // Column prefs — apply only on first arrival to avoid clobbering
        // in-session edits the user made (those are saved via setSubData).
        if (!bootstrapAppliedRef.current && data.columnPrefs) {
            const prefs = data.columnPrefs;
            const standardKeys = STANDARD_COLUMNS.map(c => c.key);
            const normalize = (savedOrder) => {
                if (!savedOrder) return null;
                const valid = savedOrder.filter(key => (standardKeys.includes(key) || key.startsWith('custom_')) && !LEGACY_TRIAGE_SCORE_COLUMNS.has(key));
                const missing = standardKeys.filter(key => !valid.includes(key));
                return missing.length > 0 ? [...valid, ...missing] : valid;
            };
            setVisibleColumns(filterLegacyTriageColumns(prefs.visibleColumns || DEFAULT_VISIBLE_COLUMNS));
            if (prefs.columnOrders && typeof prefs.columnOrders === 'object') {
                const orders = {};
                for (const [k, v] of Object.entries(prefs.columnOrders)) orders[k] = normalize(v);
                setColumnOrders(orders);
            } else if (prefs.columnOrder) {
                setColumnOrders({ all: normalize(prefs.columnOrder) });
            }
            // Mirror to localStorage for offline starts on subsequent visits.
            try { localStorage.setItem(`leads_column_preferences_${companyId}`, JSON.stringify(prefs)); } catch {}
            bootstrapAppliedRef.current = true;
        }
    }, [bootstrapQuery.data, companyId]);

    // Reset the "applied" flag when company changes so a different company's
    // prefs land on first arrival.
    useEffect(() => { bootstrapAppliedRef.current = false; }, [companyId]);

    // Offline / first-paint fallback: if bootstrap hasn't returned yet but
    // localStorage has saved prefs, hydrate from them so the first table
    // render uses the user's column layout. Bootstrap, when it lands, will
    // overwrite these (it's the source of truth).
    useEffect(() => {
        if (!companyId || bootstrapAppliedRef.current) return;
        const saved = localStorage.getItem(`leads_column_preferences_${companyId}`);
        if (!saved) return;
        try {
            const parsed = JSON.parse(saved);
            setVisibleColumns(filterLegacyTriageColumns(parsed.visibleColumns || DEFAULT_VISIBLE_COLUMNS));
            if (parsed.columnOrders && typeof parsed.columnOrders === 'object') {
                setColumnOrders(parsed.columnOrders);
            } else if (parsed.columnOrder) {
                setColumnOrders({ all: parsed.columnOrder });
            }
        } catch {}
    }, [companyId]);

    // Compatibility shim: handleRefresh and a few other callers still
    // expect a fetchLeads function. Route them through the query cache so
    // refetch behavior matches what the hooks do.
    const fetchLeads = useCallback(() => {
        queryClient.invalidateQueries({ queryKey: ['leads', companyId], exact: false });
    }, [queryClient, companyId]);

    useEffect(() => {
        if (!companyId) {
            setOnboardingEmailBootstrapJob(null);
            return undefined;
        }
        const ref = doc(getFirestore(), 'companies', companyId, 'onboardingJobs', 'firstEmailDeal');
        return onSnapshot(ref, (snap) => {
            setOnboardingEmailBootstrapJob(snap.exists() ? snap.data() : null);
        }, () => {
            setOnboardingEmailBootstrapJob(null);
        });
    }, [companyId]);

    // Initial auth check
    useEffect(() => {
        document.title = "Home | Clozers";

        const checkAuth = async () => {
            const user = await checkOnAuthStateChanged();
            if (user.status === 200) {
                setLoggedIn(true);
            } else {
                navigate("/login", { state: { path: '/' + window.location.search } });
            }
            setLoading(false);
        };

        checkAuth();
    }, [navigate]);

    // Redirect to onboarding if loading is done and company is missing
    useEffect(() => {
        if (!loading && !companyLoading && !userLoading && loggedIn && !companyId) {
            navigate('/onboarding');
        }
    }, [loading, companyLoading, userLoading, loggedIn, companyId, navigate]);

    // Redirect to integrations once, the first time a brand-new user lands with
    // no leads and nothing connected. A localStorage flag ensures we only do
    // this once — returning to /leads after that should never redirect again.
    // Key is scoped to the company so multi-account users each get their own flag.
    useEffect(() => {
        if (!unipileAccountsLoading && !companyLoading && isFirstRunEmpty && !integrationsConnected) {
            const flag = `clozers_integrations_redirected_${companyId}`;
            if (!localStorage.getItem(flag)) {
                localStorage.setItem(flag, '1');
                navigate('/integrations');
            }
        }
    }, [unipileAccountsLoading, companyLoading, isFirstRunEmpty, integrationsConnected, companyId, navigate]);

    // Track when the user enters the waiting state so we can detect first arrival
    useEffect(() => {
        if (isFirstRunEmpty && integrationsConnected && !showBootstrapState) {
            wasInWaitingRef.current = true;
        }
    }, [isFirstRunEmpty, integrationsConnected, showBootstrapState]);

    // Highlight the first row when the first lead arrives after a waiting state
    useEffect(() => {
        if (!wasInWaitingRef.current || leads.length === 0) return;
        wasInWaitingRef.current = false;
        setHighlightFirstRow(true);
        const timer = setTimeout(() => setHighlightFirstRow(false), 2000);
        return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [leads.length]);

    // Leads fetching is driven by useLeadsPage above; bootstrap fills the
    // source/sender option dropdowns. The legacy fetchSourceOptions /
    // fetchSenderOptions / leads-trigger effects that lived here have been
    // replaced by the data-layer hooks.

    // Fetch company contacts for Identify Contact modal autocomplete
    useEffect(() => {
        if (!companyId) return;
        const fetchContacts = async () => {
            try {
                const url = process.env.REACT_APP_GET_ORDERED_DOCS;
                const result = await cloudFunctionV2(url, {
                    collection: "companies",
                    subcollection: "contacts",
                    document: companyId,
                    field: 'data.lastSeenAt',
                    order: 'desc',
                    limit: 200
                });
                if (result.status === 200 && Array.isArray(result.data)) {
                    setCompanyContacts(result.data.map(d => d.data).filter(Boolean));
                }
            } catch (err) {
                console.error('Error fetching company contacts:', err);
            }
        };
        fetchContacts();
    }, [companyId]);

    // States / cities / propertyType options now come from leadsBootstrap
    // (cached for 5 min). Replaces the prior 3-call Promise.all that fired
    // on every leadType change.

    // Handle filter change
    const handleFilterChange = (filterValue) => {
        recordEvent('leads_filter_applied', { filterType: 'leadType', value: filterValue });
        setLeadTypeFilter(filterValue);
        // Clear archived filter when switching to a type filter
        if (archivedFilter) setArchivedFilter(false);
        // Clear active source/sender selections when lead category changes;
        // the option lists themselves stay populated (bootstrap-cached).
        setSelectedSources([]);
        updateSearchParams({ filter: filterValue, page: '1', sources: null, senders: null, archived: null });
    };

    // Handle address search input change (URL syncs via debounce effect below)
    const onChangeAddressSearch = (e) => {
        setAddressSearch(e.target.value);
    };

    // Clear address search
    const clearAddressSearch = () => {
        setAddressSearch('');
        updateSearchParams({ search: null, page: '1' });
    };

    // On mobile the search field is collapsed to an icon until tapped; on
    // desktop it's always shown. An active query keeps it open so a live search
    // is never stranded behind the icon. Mirrors SpencersDeals.
    const showSearchField = !isMobile || searchOpen || !!addressSearch;
    const openMobileSearch = useCallback(() => setSearchOpen(true), []);
    // The trailing × clears the text when there's some, and collapses the field
    // back to the icon when it's already empty.
    const handleSearchClearOrClose = useCallback(() => {
        if (addressSearch) {
            setAddressSearch('');
            updateSearchParams({ search: null, page: '1' });
        } else {
            setSearchOpen(false);
        }
    }, [addressSearch, updateSearchParams]);

    // Handle sorting
    const handleSort = (column) => {
        let newDir;
        if (sortColumn === column) {
            newDir = sortDirection === 'asc' ? 'desc' : 'asc';
            setSortOverride({ column, direction: newDir });
        } else {
            // dealScore belongs here because HIGHER is better: descending puts the best deals at
            // the top, which is the whole point of the column.
            // maoVsAsk joins them for the same reason: it is MAO as a percent of ask, so a HIGHER
            // number is a smaller discount, and descending puts the closest-to-ask deals on top.
            const descendingColumns = ['createdAt', 'updatedAt', 'askingPrice', 'beds', 'baths', 'sqft', 'yearBuilt', 'zillowEstimate', 'redfinEstimate', 'realtorEstimate', 'spread', 'maoVsAsking', 'dealScore', 'maoVsAsk'];
            newDir = descendingColumns.includes(column) ? 'desc' : 'asc';
            setSortOverride({ column, direction: newDir });
        }
        updateSearchParams({ sort: column, dir: newDir, page: '1' });
    };

    const handleViewToggle = useCallback((showTable) => {
        if (tableView === showTable) return;
        recordEvent('leads_view_toggled', { view: showTable ? 'table' : 'map' });
        setTableView(showTable);
        // Map and table now use separate queries (useLeadsInBounds vs
        // useLeadsPage). The map query is enabled only in map view and
        // re-runs when debouncedBounds changes — no need to clear bounds
        // state or flip a "viewportFilterActive" flag.
        setViewportFilterActive(!showTable);
        updateSearchParams({ view: showTable ? null : 'map' });
    }, [tableView, updateSearchParams]);

    // Floating Map/Table pill (mobile only) — flips between the two views, the
    // mobile stand-in for the desktop segmented toggle. Mirrors SpencersDeals'
    // toggleMobileView.
    const toggleMobileView = useCallback(() => {
        handleViewToggle(!tableView);
    }, [tableView, handleViewToggle]);

    // The lead whose card docks to the bottom of the mobile map (tap-a-pin /
    // Airbnb pattern). Only resolves in mobile map view with a live selection;
    // desktop surfaces the selection through the side rail instead, so this
    // stays null there. Mirrors SpencersDeals' selectedMapLead.
    const selectedMapLead = useMemo(() => {
        if (!isMobile || tableView || !selectedLeadId) return null;
        return mappableLeads.find((l) => l.leadId === selectedLeadId) || null;
    }, [isMobile, tableView, selectedLeadId, mappableLeads]);

    // Dismiss the bottom map card (matches tapping its marker again).
    const handleCloseMapCard = useCallback(() => setSelectedLeadId(null), []);

    // The mobile floating pill + tap-a-pin card are portaled to <body> at
    // z-index 200, so they'd paint OVER any open modal/overlay (the dark-backdrop
    // modals sit ~z11–15; the full-screen create-lead VerifyProperty overlay and
    // the image viewer are their own layers). Suppress both whenever any of those
    // is up so a floating control never floats above a dimmed page.
    const mobileOverlayActive = showDarkBackground || viewScrollableImages || createLeadVerifyProperty !== null;

    // Handle page change. No manual loading flag — `leadsLoading` is derived
    // from the query state, and `isRefetching` drives the paginate indicator.
    const onPageChange = (newPage) => {
        updateSearchParams({ page: newPage.toString() });
    };

    // Handle refresh — invalidates the cached leads query so TanStack
    // refetches with the current params. Single-arg now since the query
    // already knows what it's fetching.
    const handleRefresh = useCallback(() => {
        if (companyId) fetchLeads();
    }, [companyId, fetchLeads]);

    // First-run bootstrap recovery. The freshly-created onboarding lead only
    // surfaces when getOrderedDocs is re-run, but that query never polls and
    // the pending-lead watcher below has nothing to watch while the list is
    // empty. A single refetch on the job's status transition can lose the race
    // (getOrderedDocs cold-starts ~20s and may run before the new doc is
    // visible), leaving the "processing" overlay stuck forever. So while the
    // overlay is up and no lead has landed yet, retry on an interval. The
    // moment a lead appears, leads.length flips, the overlay clears, and the
    // cleanup tears down the interval.
    useEffect(() => {
        if (!bootstrapProcessing || leads.length > 0) return undefined;
        handleRefresh();
        const id = setInterval(handleRefresh, 4000);
        return () => clearInterval(id);
    }, [bootstrapProcessing, leads.length, handleRefresh]);

    // Self-heal a stranded onboarding scan by re-invoking applyOnboardingBuyBox
    // once. Two stranded shapes:
    //   - incomplete commit: terminal job with buy-box matches but zero created
    //     leads (the credit-gate cohort — create POSTs rejected before
    //     onboarding-scan leads were made free).
    //   - stuck applying: buyBoxApplying left latched true by an apply that was
    //     hard-killed before clearing it. The server reclaims the latch once it's
    //     stale (>150s), so a re-invoke from here unsticks it for a user who left
    //     the onboarding reveal before recovery.
    // The callable is idempotent/serialized server-side (claim transaction), so a
    // re-invoke during a genuinely in-progress apply is a harmless no-op. The
    // polling effect above surfaces the leads once they land. One-shot per mount.
    useEffect(() => {
        const j = onboardingEmailBootstrapJob;
        if (!companyId || !j || bootstrapHealRef.current) return;
        const terminal = j.status === 'complete' || j.status === 'pool_ready';
        const created = j.createdLeadIds?.length || 0;
        const matchCount = j.matchCount ?? (j.matches?.length || 0);
        const incompleteCommit = j.buyBoxApplied === true && created === 0 && matchCount > 0;
        const stuckApplying = j.buyBoxApplying === true && created === 0;
        if (terminal && (incompleteCommit || stuckApplying)) {
            bootstrapHealRef.current = true;
            httpsCallable(getFunctions(), 'applyOnboardingBuyBox')()
                .then(() => handleRefresh())
                .catch((err) => console.warn('[leads] onboarding scan self-heal failed:', err?.message || err));
        }
    }, [companyId, onboardingEmailBootstrapJob, handleRefresh]);

    // Give-up timeout: if a terminal job with matches still hasn't produced a
    // visible lead after a grace window (degenerate data, or self-heal also
    // failing), stop showing the processing spinner and fall through to the
    // waiting/empty state. Clears if a lead arrives first (leads.length flips).
    useEffect(() => {
        // Reset only when a real lead lands — NOT when bootstrapProcessing goes
        // false because we just gave up (that would flip the flag back and
        // reopen the overlay in a loop).
        if (leads.length > 0) {
            setBootstrapGaveUp(false);
            return undefined;
        }
        if (!bootstrapProcessing) return undefined;
        const t = setTimeout(() => setBootstrapGaveUp(true), 30000);
        return () => clearTimeout(t);
    }, [bootstrapProcessing, leads.length]);

    // Event-driven refresh for leads whose pipeline isn't fully settled yet:
    //   - status queued/processing — enrichment still running (skeleton cells)
    //   - status complete but underwriting still in flight — saveLeadBatched
    //     flips status='complete' BEFORE processUnderwriting runs (~7 min for
    //     the ARV agent), so without watching these the ARV / repair-cost
    //     cells stay red-X until manual refresh.
    // Instead of polling the heavy getOrderedDocs endpoint on a timer, we open
    // a Firestore onSnapshot per pending lead; the moment one settles we
    // refetch the page once. Debounced so a batch of completions coalesces
    // into a single getOrderedDocs call. Listeners are idle until a doc
    // actually changes and tear down when the pending set changes (the key
    // bakes in underwriting.status so an underwriting-landed write changes
    // the key on the next refetch).
    // The deal score is in the key for the same reason underwriting.status is: it is
    // one of the states that ends the wait, so a score landing has to re-key the
    // watcher on the next refetch or the listener set goes stale against the page.
    const pendingLeadKey = useMemo(
        () => (leads || [])
            .filter(l => l.leadId && (l.status === 'queued' || l.status === 'processing' || isUnderwritingPending(l)))
            .map(l => `${l.leadId}:${l.status || ''}:${l.underwriting?.status || ''}:${isCoLivingScorePending(l) ? 'score' : ''}`)
            .join(','),
        [leads]
    );
    useEffect(() => {
        if (!companyId || !pendingLeadKey) return undefined;
        // pendingLeadKey entries are `${leadId}:${leadStatus}:${uwStatus}` —
        // we keep leadStatus because it tells us WHY the lead is in the
        // watcher set, which determines what transitions warrant a refresh.
        // Without that distinction, a lead added for "pending underwriting"
        // (leadStatus already 'complete') would refresh on its first snapshot
        // — which always shows status='complete' — looping every 1.5 s.
        const entries = pendingLeadKey.split(',').map(k => {
            const [leadId, leadStatus, , scoreFlag] = k.split(':');
            return {
                leadId,
                wasInflight: leadStatus === 'queued' || leadStatus === 'processing',
                // Why the lead is in the set matters here exactly as wasInflight does:
                // an entry waiting on the SCORE must not be woken by underwriting
                // settling, because for these leads underwriting settled before they
                // were ever added. Only the score ends their wait.
                //
                // `&& !wasInflight` because the two reasons can overlap — a re-ingested
                // lead can be back in queued/processing while still carrying a bridge
                // path and no score. Enrichment landing is the nearer transition and
                // must keep its existing wake-up; the lead re-keys as score-pending on
                // the refetch that follows, so nothing is lost by deferring.
                awaitingScore: scoreFlag === 'score' && !(leadStatus === 'queued' || leadStatus === 'processing'),
            };
        });
        const db = getFirestore();
        let disposed = false;
        let debounceId = null;
        const scheduleRefresh = () => {
            if (debounceId) clearTimeout(debounceId);
            debounceId = setTimeout(() => { if (!disposed) handleRefresh(); }, 1500);
        };
        const unsubs = entries.map(({ leadId, wasInflight, awaitingScore }) => {
            const ref = doc(db, 'companies', companyId, 'leads', leadId);
            // Fire on transitions worth a refresh (first snapshot included, in
            // case the lead settled between the page fetch and this listener
            // attaching):
            //  1. wasInflight && status leaving queued/processing — enrichment
            //     just landed (existing behaviour, unchanged for that path).
            //  2. underwriting hitting a terminal state (any of: terminal
            //     underwriting.status / syncedAt set / arv set) — covers the
            //     ~7-min ARV-agent window where status='complete' is already
            //     true but the dotted-path underwriting update at
            //     enrichmentProcessor.js:2525-2532 hasn't landed yet.
            // For !wasInflight (added due to pending underwriting), we
            // deliberately DO NOT refresh on statusSettled — the lead's
            // status='complete' is always-true for those entries; refreshing
            // on it would loop until underwriting lands.
            return onSnapshot(ref, (snap) => {
                if (!snap.exists()) { scheduleRefresh(); return; }
                const data = snap.data()?.data;
                const s = data?.status;
                const statusSettled = wasInflight && s && s !== 'queued' && s !== 'processing';
                const uw = data?.underwriting;
                const underwritingSettled = !!uw && (
                    (uw.status && TERMINAL_UNDERWRITING_STATUSES.has(uw.status))
                    || uw.syncedAt != null
                    || uw.arv != null
                );
                // The score landing (or the path leaving bridgeRefi, which retires the
                // claim that one is owed) is what re-sorts the table — syncLeadUnderwriting
                // mirrors both onto the lead doc, so this listener sees them. Without
                // this the phase-2 write happened with nobody watching and a new top
                // deal sat at the bottom, unscored, until a manual refresh.
                const scoreSettled = data?.coLivingDealScore != null
                    || data?.coLivingFinancingPath !== 'bridgeRefi';
                if (awaitingScore ? scoreSettled : (statusSettled || underwritingSettled)) {
                    scheduleRefresh();
                }
            }, (err) => console.error('lead status watcher error:', err));
        });
        return () => {
            disposed = true;
            if (debounceId) clearTimeout(debounceId);
            unsubs.forEach(u => u());
        };
    }, [companyId, pendingLeadKey, handleRefresh]);

    // Companion to the pinned-rows logic (tableData): refetch the page when a
    // pinned in-flight lead LEAVES the live set (completed / failed / aged-out).
    // The pendingLeadKey watcher above only covers pending leads already in
    // `leads`, but most pinned rows are NOT in `leads`: getOrderedDocs drops
    // address-less queued leads (the orphan-queued filter), and a just-created
    // lead isn't in the page yet. Without this, a pinned row would drop out of
    // liveProcessingLeads on completion and vanish until a manual refresh,
    // instead of settling into its sorted, completed position.
    //
    // Why a dedicated drop-detector instead of folding the ids into
    // pendingLeadKey: liveProcessingLeads updates the instant a status flips,
    // which would change the key and tear down that watcher — clearing its
    // debounced refresh before it fires. This effect refetches directly.
    const prevLiveLeadIdsRef = useRef(new Set());
    useEffect(() => {
        const currentIds = new Set(liveProcessingLeads.map(l => l.leadId).filter(Boolean));
        let dropped = false;
        prevLiveLeadIdsRef.current.forEach((id) => { if (!currentIds.has(id)) dropped = true; });
        prevLiveLeadIdsRef.current = currentIds;
        if (dropped) handleRefresh();
    }, [liveProcessingLeads, handleRefresh]);

    // Refs to avoid stale closures in archive callbacks
    const leadsRef = useRef(leads);
    leadsRef.current = leads;
    const archiveToastRef = useRef(archiveToast);
    archiveToastRef.current = archiveToast;
    // Map<leadId, { unsubscribe, timeoutId }> for in-flight createLead watchers.
    // createLeadManual returns immediately but enrichment runs async (~30-60s);
    // we watch the placeholder doc and refresh the leads list when it completes
    // or gets deleted (merge into existing lead), so the user doesn't have to
    // refresh manually.
    const createLeadWatchersRef = useRef(new Map());
    // Live image-download watchers keyed by leadId — drive the imageToast's
    // stage copy (downloading → analyzing) and dismiss it when the lead doc
    // settles to complete/failed.
    const imageDownloadWatchersRef = useRef(new Map());

    // Tear down any in-flight watchers on unmount
    useEffect(() => {
        const watchers = createLeadWatchersRef.current;
        const imageWatchers = imageDownloadWatchersRef.current;
        return () => {
            watchers.forEach(({ unsubscribe, timeoutId }) => {
                clearTimeout(timeoutId);
                unsubscribe();
            });
            watchers.clear();
            imageWatchers.forEach(({ unsubscribe, timeoutId }) => {
                clearTimeout(timeoutId);
                unsubscribe();
            });
            imageWatchers.clear();
            if (createLeadFailedToastTimerRef.current) {
                clearTimeout(createLeadFailedToastTimerRef.current);
                createLeadFailedToastTimerRef.current = null;
            }
        };
    }, []);

    // Archive a lead: write isArchived to Firestore, optimistically remove from list, show toast
    const handleArchiveLead = useCallback(async (leadId) => {
        if (!companyId || !leadId) return;

        // Find the lead's address for the toast message
        const lead = leadsRef.current.find(l => l.leadId === leadId || l.propertyId === leadId);
        const address = lead?.address?.streetAddress || 'Lead';

        // Optimistically remove from local state
        setLeads(prev => prev.filter(l => (l.leadId || l.propertyId) !== leadId));
        setLeadsTotal(prev => Math.max(0, prev - 1));

        // Write to Firestore
        try {
            const db = getFirestore();
            const leadRef = doc(db, "companies", companyId, "leads", leadId);
            const now = new Date();
            await updateDoc(leadRef, {
                'data.isArchived': true,
                'data.archivedBy': user?.userId || null,
                'data.archivedAt': now,
                'data.updatedAt': now
            });
            recordEvent('lead_archived', { propertyId: lead?.propertyId || null, leadId });
        } catch (error) {
            console.error('Error archiving lead:', error);
            handleRefresh();
            return;
        }

        // Clear any existing toast timer
        if (archiveToastRef.current?.timer) {
            clearTimeout(archiveToastRef.current.timer);
        }

        // Show toast with 10s auto-dismiss
        const timer = setTimeout(() => {
            setArchiveToast(null);
        }, 10000);
        setArchiveToast({ leadId, propertyId: lead?.propertyId || null, address, timer });
    }, [companyId, handleRefresh, user?.userId]);

    // Restore a lead: remove isArchived from Firestore, refresh list
    const handleRestoreLead = useCallback(async (leadId) => {
        if (!companyId || !leadId) return;

        // Optimistically remove from archived view
        setLeads(prev => prev.filter(l => (l.leadId || l.propertyId) !== leadId));
        setLeadsTotal(prev => Math.max(0, prev - 1));

        try {
            const db = getFirestore();
            const leadRef = doc(db, "companies", companyId, "leads", leadId);
            await updateDoc(leadRef, {
                'data.isArchived': deleteField(),
                'data.archivedBy': deleteField(),
                'data.archivedAt': deleteField(),
                'data.updatedAt': new Date()
            });
        } catch (error) {
            console.error('Error restoring lead:', error);
            handleRefresh();
        }
    }, [companyId, handleRefresh]);

    // Undo archive: restore the lead and dismiss toast
    const handleUndoArchive = useCallback(async () => {
        if (!archiveToast) return;
        const { leadId, propertyId: archivedPropertyId, timer } = archiveToast;
        if (timer) clearTimeout(timer);
        setArchiveToast(null);

        // Write restore to Firestore then refresh to bring the lead back
        if (companyId && leadId) {
            try {
                const db = getFirestore();
                const leadRef = doc(db, "companies", companyId, "leads", leadId);
                await updateDoc(leadRef, {
                    'data.isArchived': deleteField(),
                    'data.archivedBy': deleteField(),
                    'data.archivedAt': deleteField(),
                    'data.updatedAt': new Date()
                });
                recordEvent('lead_archive_undone', { propertyId: archivedPropertyId, leadId });
            } catch (error) {
                console.error('Error undoing archive:', error);
            }
            handleRefresh();
        }
    }, [archiveToast, companyId, handleRefresh]);

    // Handle archived filter toggle (currently unused — FilterChip is commented out)
    // const handleArchivedToggle = useCallback(() => {
    //     setArchivedFilter(prev => {
    //         const next = !prev;
    //         if (next) {
    //             setLeadTypeFilter(null);
    //             setSelectedSources([]);
    //             setAvailableSources([]);
    //             setSourceDropdownOpen(false);
    //             setAvailableSenders([]);
    //             updateSearchParams({ archived: '1', filter: null, sources: null, senders: null, page: '1' });
    //         } else {
    //             updateSearchParams({ archived: null, page: '1' });
    //         }
    //         return next;
    //     });
    // }, [updateSearchParams]);

    // Viewport change handler (called from LeadsMap onIdle)
    // Always sets bounds — viewport filtering is automatic in map view
    const handleViewportChange = useCallback((bounds) => {
        setViewportBounds(bounds);
    }, []);

    // (Source dropdown was removed when the toolbar was simplified; the
    // multiselect lives in LeadsFilterModal now. selectedSources state +
    // the `sources` URL param are still wired so URLs sent from elsewhere
    // continue to filter — only the inline toolbar UI is gone.)

    // Hide the lead-type tabs entirely unless we've confirmed the company
    // has BOTH segments. Default while bootstrap is loading is false so
    // single-type accounts never see the tabs flash on then disappear.
    const showLeadTypeChips = useMemo(() => {
        if (!availableLeadTypes) return false;
        return availableLeadTypes.includes('direct_to_seller')
            && availableLeadTypes.includes('property_lead');
    }, [availableLeadTypes]);

    // Clear any stale leadTypeFilter (e.g. from a bookmarked URL) once we
    // discover the company only has one type — the chips that would let the
    // user undo it are gone.
    useEffect(() => {
        if (!availableLeadTypes || showLeadTypeChips || !leadTypeFilter) return;
        setLeadTypeFilter(null);
        updateSearchParams({ filter: null, page: '1' });
    }, [availableLeadTypes, showLeadTypeChips, leadTypeFilter, updateSearchParams]);

    // Sender filter handlers (searchable multiselect in filter modal)
    const filteredSenderResults = useMemo(() => {
        if (!filterSenderSearch) return [];
        const lower = filterSenderSearch.toLowerCase();
        return availableSenders
            .filter(s => s.toLowerCase().includes(lower) && !filterSenders.includes(s))
            .map(s => ({ label: s }));
    }, [filterSenderSearch, availableSenders, filterSenders]);

    const handleSelectFilterSender = useCallback((item) => {
        setFilterSenders(prev => prev.includes(item.label) ? prev : [...prev, item.label]);
        setFilterSenderSearch('');
    }, []);

    const handleClearFilterSenderSearch = useCallback(() => {
        setFilterSenderSearch('');
    }, []);

    const handleRemoveFilterSender = useCallback((name) => {
        setFilterSenders(prev => prev.filter(v => v !== name));
    }, []);

    // Handle row click — navigation is handled by LeadsTable internally;
    // this callback is reserved for any additional side-effects on row click.
    const handleRowClick = useCallback(() => {
        // Navigation + leadsSearch state is handled inside LeadsTable's onClick.
    }, []);

    // Handle lead field update from inline editing
    const handleLeadUpdate = useCallback((leadId, field, newValue, imageFolders) => {
        setLeads(prevLeads => prevLeads.map(lead => {
            if (lead.leadId !== leadId) return lead;

            const updatedLead = { ...lead };
            switch (field) {
                case 'beds':
                    updatedLead.verifiedCharacteristics = {
                        ...updatedLead.verifiedCharacteristics,
                        bedrooms: newValue
                    };
                    break;
                case 'baths':
                    updatedLead.verifiedCharacteristics = {
                        ...updatedLead.verifiedCharacteristics,
                        bathrooms: newValue
                    };
                    break;
                case 'sqft':
                    updatedLead.verifiedCharacteristics = {
                        ...updatedLead.verifiedCharacteristics,
                        livingSquareFeet: newValue
                    };
                    break;
                case 'askingPrice': {
                    updatedLead.currentAskingPrice = newValue;
                    // Recompute local computedSpread to stay in sync with Firestore
                    const ests = [updatedLead.estimates?.zillow, updatedLead.estimates?.redfin, updatedLead.estimates?.realtor]
                        .filter(v => v != null && !isNaN(v));
                    if (ests.length > 0 && newValue) {
                        ests.sort((a, b) => a - b);
                        const mid = Math.floor(ests.length / 2);
                        const median = ests.length % 2 === 0 ? (ests[mid - 1] + ests[mid]) / 2 : ests[mid];
                        updatedLead.computedSpread = Math.round(((median - newValue) / newValue) * 1000) / 10;
                    } else {
                        updatedLead.computedSpread = null;
                    }
                    // Recompute OUR price's gap vs the ask the way the server will (gapAfterAskEdit,
                    // LeadsTable.js). 🚨 The server measures against ingestedAskingPrice FIRST, so on a
                    // lead that has one this edit leaves the gap alone. This used to recompute the FLIP
                    // MAO against the edited box, showing a number the next refresh took back.
                    updatedLead.computedMaoVsAsking = gapAfterAskEdit(updatedLead, newValue);
                    break;
                }
                case 'image':
                    updatedLead.imageUrls = newValue ? [newValue] : [];
                    if (imageFolders !== undefined) {
                        updatedLead.imageFolders = imageFolders;
                    }
                    break;
                case 'leadReviewStatus':
                    updatedLead.leadReviewStatus = newValue;
                    break;
                case 'dealStatus':
                    updatedLead.dealStatus = newValue;
                    break;
                case 'acquisitionNotes':
                    updatedLead.acquisitionNotes = newValue;
                    break;
                case 'acquisitionComments':
                    updatedLead.acquisitionComments = newValue;
                    break;
                case 'currentSender':
                    updatedLead.currentSender = newValue;
                    break;
                case 'originalSender':
                    updatedLead.originalSender = newValue;
                    break;
                case 'assigned':
                    if (newValue && typeof newValue === 'object' && newValue.assignedRoles) {
                        updatedLead.assignedRoles = newValue.assignedRoles;
                        updatedLead.assignedUserIds = newValue.assignedUserIds;
                        updatedLead.assignedTo = newValue.assignedTo;
                    } else {
                        updatedLead.assignedTo = newValue;
                    }
                    break;
                default:
                    break;
            }
            return updatedLead;
        }));
    }, []);

    // Image URL modal handlers
    const openImageUrlModal = useCallback((leadId, currentUrl) => {
        setImageUrlModalLeadId(leadId);
        setImageUrlModalValue(currentUrl || '');
        setImageUrlModalOpen(true);
    }, []);

    const closeImageUrlModal = useCallback(() => {
        setImageUrlModalOpen(false);
        setImageUrlModalLeadId(null);
        setImageUrlModalValue('');
    }, []);

    // Validate URL
    const isValidUrl = (url) => {
        if (!url || url.trim() === '') return false;
        try {
            new URL(url);
            return true;
        } catch {
            return false;
        }
    };

    // Save image URL to Firebase
    const saveImageUrl = useCallback(async () => {
        if (!companyId || !imageUrlModalLeadId || !isValidUrl(imageUrlModalValue)) {
            return;
        }

        setImageUrlModalLoading(true);

        const url = imageUrlModalValue.trim();
        const provider = getImageFolderProvider(url);

        try {
            const db = getFirestore();
            const leadRef = doc(db, "companies", companyId, "leads", imageUrlModalLeadId);
            const startedAt = new Date();

            // Always seed imageDownloadStatus = 'processing'. The server-side
            // dispatchLeadImageDownload trigger fires on that transition and
            // invokes runDownloadLeadImages, which calls resolveAndPromoteUrls
            // to expand shorteners (bit.ly → Drive folder, ESP tracker → Drive)
            // before collection. So even when the client-side substring matcher
            // doesn't recognize a provider, we still want the server to try.
            const processingStatus = {
                state: 'processing',
                startedAt,
                completedAt: null,
                imageCount: null,
                error: null
            };
            const updateData = {
                'data.imageUrls': [url],
                'data.updatedAt': startedAt,
                'data.imageDownloadStatus': processingStatus
            };

            // If URL is Drive, Dropbox or OneDrive, pre-populate imageFolders
            // for snappy UI. Otherwise rely on runDownloadLeadImages's
            // resolveAndPromoteUrls pass to bucketize on the server.
            if (provider) {
                const lead = leads.find(l => l.leadId === imageUrlModalLeadId);
                const existing = lead?.imageFolders || { drive: [], dropbox: [], oneDrive: [] };
                const newFolders = {
                    drive: [...(existing.drive || [])],
                    dropbox: [...(existing.dropbox || [])],
                    oneDrive: [...(existing.oneDrive || [])]
                };
                newFolders[provider] = [url];
                updateData['data.imageFolders'] = newFolders;
                handleLeadUpdate(imageUrlModalLeadId, 'image', url, newFolders);
            } else {
                handleLeadUpdate(imageUrlModalLeadId, 'image', url);
            }

            await updateDoc(leadRef, updateData);

            // Close modal
            closeImageUrlModal();

            // Server-side dispatchLeadImageDownload Firestore trigger picks up
            // the state='processing' transition and runs runDownloadLeadImages
            // on the server. The downloader's first action is to call
            // resolveAndPromoteUrls — so bit.ly / t.co / ESP-tracker links in
            // imageUrls become canonical Drive/Dropbox/OneDrive folders before
            // the download attempt.
            {
                const lead = leads.find(l => l.leadId === imageUrlModalLeadId);
                const address = lead?.address?.streetAddress || 'Lead';
                const watchedId = imageUrlModalLeadId;

                // Tear down any prior watcher for this lead (re-adding a link).
                const prior = imageDownloadWatchersRef.current.get(watchedId);
                if (prior) { clearTimeout(prior.timeoutId); prior.unsubscribe(); imageDownloadWatchersRef.current.delete(watchedId); }

                const clearWatcher = () => {
                    const entry = imageDownloadWatchersRef.current.get(watchedId);
                    if (!entry) return;
                    clearTimeout(entry.timeoutId);
                    entry.unsubscribe();
                    imageDownloadWatchersRef.current.delete(watchedId);
                };
                const dismissIfMine = () => setImageToast(prev => (prev?.leadId === watchedId ? null : prev));

                setImageToast({ leadId: watchedId, message: `Downloading photos for ${address}…` });

                const watchRef = doc(db, 'companies', companyId, 'leads', watchedId);
                // Safety net: clear the toast after 10 min even if the doc never
                // settles (e.g. trigger dropped during a deploy).
                const timeoutId = setTimeout(() => { clearWatcher(); dismissIfMine(); }, 600000);
                const unsubscribe = onSnapshot(watchRef, (snap) => {
                    if (!snap.exists()) { clearWatcher(); dismissIfMine(); return; }
                    const st = snap.data()?.data?.imageDownloadStatus;
                    if (!st || st.state === 'processing') {
                        const label = getImageDownloadStageLabel(st) || 'Downloading photos';
                        setImageToast(prev => (prev?.leadId === watchedId
                            ? { leadId: watchedId, message: `${label} for ${address}…` }
                            : prev));
                        return;
                    }
                    if (st.state === 'complete') {
                        setImageToast({ leadId: watchedId, message: `Photos ready for ${address}` });
                        handleRefresh();
                        setTimeout(dismissIfMine, 4000);
                    } else if (st.state === 'failed') {
                        setImageToast({ leadId: watchedId, message: `Couldn't download photos for ${address}` });
                        setTimeout(dismissIfMine, 5000);
                    }
                    clearWatcher();
                }, (err) => {
                    console.error('image download watcher error:', err);
                    clearWatcher();
                    dismissIfMine();
                });
                imageDownloadWatchersRef.current.set(watchedId, { unsubscribe, timeoutId });
            }
        } catch (error) {
            console.error('Error saving image URL:', error);
        } finally {
            setImageUrlModalLoading(false);
        }
    }, [companyId, imageUrlModalLeadId, imageUrlModalValue, handleLeadUpdate, closeImageUrlModal, leads, handleRefresh]);

    // Identify Contact modal handlers
    const openContactModal = useCallback((leadId, rawData) => {
        setContactModalLeadId(leadId);
        setContactModalRawData(rawData);
        setContactModalValues({ name: '', company: '', email: '', phone: '' });
        setContactModalOpen(true);
    }, []);

    const closeContactModal = useCallback(() => {
        setContactModalOpen(false);
        setContactModalLeadId(null);
        setContactModalRawData(null);
        setContactModalValues({ name: '', company: '', email: '', phone: '' });
    }, []);

    const saveContact = useCallback(async () => {
        if (!companyId || !contactModalLeadId) return;
        const { name, company, email, phone } = contactModalValues;
        if (!name && !company && !email && !phone) return;

        setContactModalLoading(true);
        try {
            const db = getFirestore();
            const leadRef = doc(db, "companies", companyId, "leads", contactModalLeadId);
            const senderObj = {
                name: name || null,
                company: company || null,
                email: email || null,
                phone: phone || null
            };
            await updateDoc(leadRef, {
                'data.currentSender': senderObj,
                'data.originalSender': senderObj,
                'data.senderMissing': false,
                'data.updatedAt': new Date()
            });

            // Optimistic local update
            setLeads(prevLeads => prevLeads.map(lead => {
                if (lead.leadId !== contactModalLeadId) return lead;
                return { ...lead, currentSender: senderObj, originalSender: senderObj, senderMissing: false };
            }));

            closeContactModal();
        } catch (error) {
            console.error('Error saving contact:', error);
        } finally {
            setContactModalLoading(false);
        }
    }, [companyId, contactModalLeadId, contactModalValues, closeContactModal]);

    // ==================== Create Lead handlers (textarea flow) ====================

    const openCreateLeadModal = useCallback((prefillAddress) => {
        const address = typeof prefillAddress === 'string' ? prefillAddress : undefined;
        createLeadFlowTokenRef.current++;
        setCreateLeadModalOpen(true);
        setCreateLeadText(address || '');
        setCreateLeadError(null);
        setCreateLeadLoading(false);
        setCreateLeadLoadingMessage(null);
        setCreateLeadLoadingSteps(null);
        setCreateLeadVerifyProperty(null);
        setCreateLeadExtracted(null);
        setCreateLeadPreviewPending(false);
        setCreateLeadMode('address');
        resetCreateLeadAddress();
        if (address) {
            setCreateLeadAddressQuery(address, { immediate: true });
        }
    }, [setCreateLeadAddressQuery, resetCreateLeadAddress]);

    const closeCreateLeadModal = useCallback(() => {
        createLeadFlowTokenRef.current++;
        setCreateLeadModalOpen(false);
        setCreateLeadLoading(false);
        setCreateLeadLoadingMessage(null);
        setCreateLeadLoadingSteps(null);
        setCreateLeadError(null);
        setCreateLeadText('');
        setCreateLeadVerifyProperty(null);
        setCreateLeadExtracted(null);
        setCreateLeadPreviewPending(false);
        setCreateLeadMode('address');
        resetCreateLeadAddress();
    }, [resetCreateLeadAddress]);

    // Switching tabs preserves the typed text (user might be experimenting)
    // but clears the autocomplete query in Deal mode (no dropdown there
    // anyway) and re-fires it in Address mode (so suggestions come back).
    const handleCreateLeadModeChange = useCallback((nextMode) => {
        const safeMode = nextMode === 'deal' ? 'deal' : 'address';
        setCreateLeadMode(safeMode);
        setCreateLeadError(null);
        if (safeMode === 'address') {
            // Re-fire autocomplete if there's text to search.
            const text = createLeadText || '';
            if (text.trim().length > 0) {
                setCreateLeadAddressQuery(text);
            } else {
                resetCreateLeadAddress();
            }
        } else {
            resetCreateLeadAddress();
        }
    }, [createLeadText, setCreateLeadAddressQuery, resetCreateLeadAddress]);

    const handleCreateLeadTextChange = useCallback((text) => {
        setCreateLeadText(text);
        setCreateLeadError(null);
        // Mode is the explicit signal now (replaces the old shape-detector
        // heuristic). Address tab fires autocomplete on every change; Deal
        // tab never fires autocomplete (paste auto-submits, typed prose hits
        // Continue → parse path).
        if (createLeadMode === 'address') {
            setCreateLeadAddressQuery(text);
        } else {
            resetCreateLeadAddress();
        }
    }, [createLeadMode, setCreateLeadAddressQuery, resetCreateLeadAddress]);

    // Fetches REAPI propertyDetails for a chosen address, then layers any
    // parser-extracted overrides (beds/baths/sqft/year/amenities) onto the
    // returned propertyInfo. Either opens VerifyProperty directly (pick
    // path — user explicitly chose the address) or stages a preview-card
    // confirmation step (parse path — user pasted prose, needs to confirm
    // we matched the right property before we open the heavier overlay).
    //
    // Token-guarded: caller passes the flow token captured at the start of its
    // own async chain; every state mutation below bails if the token has
    // moved on (modal cancelled / re-opened mid-flight).
    // Paste flows pass `loadingStepsForFetch` so the staged-progress checklist
    // (step 3 active) replaces the legacy single-message row. The address-tab
    // pick path leaves it null and keeps the simpler "Loading property details…"
    // line, since there's no multi-stage story to show.
    const fetchReapiAndOpenVerify = useCallback(async (item, extractedOverrides, token, previewMode = false, loadingStepsForFetch = null) => {
        const flowToken = typeof token === 'number' ? token : createLeadFlowTokenRef.current;
        setCreateLeadLoading(true);
        if (loadingStepsForFetch) {
            setCreateLeadLoadingSteps(loadingStepsForFetch);
            setCreateLeadLoadingMessage(null);
        } else {
            setCreateLeadLoadingMessage('Loading property details…');
            setCreateLeadLoadingSteps(null);
        }
        setCreateLeadError(null);
        try {
            const url = process.env.REACT_APP_REAPI_PROPERTY_DETAILS_URL;
            const result = await cloudFunctionV2(url, {
                propertyId: item.id,
                latitude: item.latitude,
                longitude: item.longitude
            });
            if (createLeadFlowTokenRef.current !== flowToken) return;
            if (result.status !== 200 || !result.data) {
                setCreateLeadError(result.data?.error || result.error || 'Could not load property details.');
                setCreateLeadLoading(false);
                setCreateLeadLoadingMessage(null);
                setCreateLeadLoadingSteps(null);
                return;
            }
            const property = result.data;
            if (extractedOverrides) {
                const info = { ...(property.propertyInfo || {}) };
                if (typeof extractedOverrides.bedrooms === 'number') info.bedrooms = extractedOverrides.bedrooms;
                if (typeof extractedOverrides.bathrooms === 'number') info.bathrooms = extractedOverrides.bathrooms;
                if (typeof extractedOverrides.squareFeet === 'number') info.livingSquareFeet = extractedOverrides.squareFeet;
                if (typeof extractedOverrides.yearBuilt === 'number') info.yearBuilt = extractedOverrides.yearBuilt;
                if (extractedOverrides.pool === true) info.pool = true;
                if (extractedOverrides.adu === true) info.adu = true;
                if (extractedOverrides.garage === true) {
                    info.garage = true;
                    if (typeof extractedOverrides.garageSpaces === 'number') info.garageSpaces = extractedOverrides.garageSpaces;
                    if (extractedOverrides.garageType) info.garageType = extractedOverrides.garageType;
                }
                property.propertyInfo = info;
            }
            // Fire checkSubjectAmenities fire-and-forget so the verify overlay's
            // Firestore listener can populate Zillow/Redfin/Realtor source chips
            // and the MLS image gallery. Same pattern as AddressSearch's
            // queryPropertyAmenities; idempotent — skipped when amenity data is
            // already present on the property_searches doc.
            (async () => {
                try {
                    const propertyId = String(item.id);
                    const existing = await getDocument('property_searches', propertyId);
                    const hasAmenityData = existing.status === 200
                        && existing.data?.data?.description !== undefined;
                    if (hasAmenityData) return;
                    const amenitiesUrl = process.env.REACT_APP_CHECK_SUBJECT_AMENITIES;
                    if (!amenitiesUrl) return;
                    cloudFunctionV2(amenitiesUrl, {
                        address: property?.propertyInfo?.address?.label,
                        latitude: property?.propertyInfo?.latitude,
                        longitude: property?.propertyInfo?.longitude,
                        propertyId,
                        model: 'gpt-5-mini',
                        reasoningLevel: 'low'
                    });
                } catch (err) {
                    console.error('checkSubjectAmenities dispatch failed:', err);
                }
            })();
            setCreateLeadVerifyProperty(property);
            setCreateLeadExtracted(extractedOverrides || null);
            setCreateLeadPreviewPending(previewMode === true);
            setCreateLeadLoading(false);
            setCreateLeadLoadingMessage(null);
            setCreateLeadLoadingSteps(null);
        } catch (err) {
            if (createLeadFlowTokenRef.current !== flowToken) return;
            console.error('REAPI lookup failed:', err);
            setCreateLeadError(err?.message || 'Failed to load property details.');
            setCreateLeadLoading(false);
            setCreateLeadLoadingMessage(null);
            setCreateLeadLoadingSteps(null);
        }
    }, []);

    const handlePickCreateLeadSuggestion = useCallback((item) => {
        const token = createLeadFlowTokenRef.current;
        // Pick path: skip preview — user explicitly chose this address.
        fetchReapiAndOpenVerify(item, null, token, false);
    }, [fetchReapiAndOpenVerify]);

    // Free-text submit: parseLeadText → resolve extracted address via REAPI
    // autocomplete → take top match → fetchReapiAndOpenVerify with the parser
    // result as overrides. Same VerifyProperty step as the pick path; extracted
    // sender/notes/price flow through createLeadExtracted into the final
    // createLeadManual payload.
    // Accepts an optional `overrideText` so the onPaste handler can submit
    // the pasted text *immediately* — without waiting for the textarea's
    // onChange to update `createLeadText` state (React batches; reading
    // state inside the handler would still see the pre-paste value).
    const handleSubmitCreateLeadText = useCallback(async (overrideText) => {
        const sourceText = typeof overrideText === 'string' ? overrideText : createLeadText;
        const text = sourceText.trim();
        if (text.length === 0) {
            // Paste flow flips loading=true via onPasteStart *before* this
            // runs; clear it on every early-return path so the user isn't
            // stranded on the "Reading your text…" spinner.
            setCreateLeadLoading(false);
            setCreateLeadLoadingMessage(null);
            setCreateLeadLoadingSteps(null);
            return;
        }
        if (!companyId) {
            setCreateLeadError('Company not loaded — refresh the page and try again.');
            setCreateLeadLoading(false);
            setCreateLeadLoadingMessage(null);
            setCreateLeadLoadingSteps(null);
            return;
        }

        const flowToken = createLeadFlowTokenRef.current;
        // Always fires for any paste-flow submission so existing volume
        // dashboards keep working — the LLM-vs-short-circuit split is tracked
        // additively by `create_lead_text_short_circuit` below.
        recordEvent('create_lead_text_submitted', { textLength: text.length });

        // Short-circuit: paste already looks like a bare US address. Skip the
        // LLM entirely (saves ~2-5s) and go straight to REAPI autocomplete +
        // property-details. On miss we surface the error immediately rather
        // than fall back to parseLeadText — false positives on the regex are
        // rare and a doubled wall-clock is worse than a fast failure.
        if (looksLikeBareAddress(text)) {
            recordEvent('create_lead_text_short_circuit', { textLength: text.length });
            setCreateLeadLoading(true);
            setCreateLeadError(null);
            // Step 1 ('Reading your text') jumps straight to complete — the
            // LLM never ran. Step 2 is active.
            setCreateLeadLoadingSteps(buildPasteLoadingSteps('complete', 'active', 'pending', { read: 'Address detected' }));
            setCreateLeadLoadingMessage(null);
            try {
                const autoUrl = process.env.REACT_APP_REAPI_AUTOCOMPLETE_URL;
                const autoRes = await cloudFunctionV2(autoUrl, { address: text });
                if (createLeadFlowTokenRef.current !== flowToken) return;
                if (autoRes.status !== 200 || !Array.isArray(autoRes.data) || autoRes.data.length === 0) {
                    setCreateLeadError(`Couldn't find "${text}" — double-check the spelling, or use the Address tab to search.`);
                    setCreateLeadLoading(false);
                    setCreateLeadLoadingMessage(null);
                    setCreateLeadLoadingSteps(null);
                    return;
                }
                const top = autoRes.data[0];
                // No overrides (no LLM extraction), previewMode=false — match
                // handlePickCreateLeadSuggestion's direct-to-VerifyProperty
                // behaviour. The user pasted a bare address; there's nothing
                // to preview-confirm.
                await fetchReapiAndOpenVerify({
                    label: top.address,
                    id: top.id,
                    countyId: top.fips,
                    latitude: top.latitude,
                    longitude: top.longitude,
                    zipcode: top.zip
                }, null, flowToken, false, buildPasteLoadingSteps('complete', 'complete', 'active', {
                    read: 'Address detected',
                    find: top.address
                }));
            } catch (err) {
                if (createLeadFlowTokenRef.current !== flowToken) return;
                console.error('short-circuit autocomplete error:', err);
                setCreateLeadError("Couldn't reach the address service. Try again in a moment.");
                setCreateLeadLoading(false);
                setCreateLeadLoadingMessage(null);
                setCreateLeadLoadingSteps(null);
            }
            return;
        }

        // Prose / mangled-address path — full parseLeadText pipeline.
        setCreateLeadLoading(true);
        setCreateLeadLoadingSteps(buildPasteLoadingSteps('active', 'pending', 'pending'));
        setCreateLeadLoadingMessage(null);
        setCreateLeadError(null);

        try {
            const fns = getFunctions();
            const parseFn = httpsCallable(fns, 'parseLeadText');
            const parseRes = await parseFn({ text });
            if (createLeadFlowTokenRef.current !== flowToken) return;
            if (parseRes.data?.status !== 200 || !parseRes.data?.data) {
                // Map backend status to a friendly message — never surface the
                // raw `error` string (it can be OpenAI/network errors or terse
                // codes like 'Unauthorized' that mean nothing to the user).
                const backendStatus = parseRes.data?.status;
                const backendError = parseRes.data?.error;
                if (backendError) console.warn('[parseLeadText] backend error:', backendStatus, backendError);
                let friendly;
                if (backendStatus === 401) friendly = "You're signed out — refresh and try again.";
                else if (backendStatus === 502) friendly = "Couldn't extract a property from that text. Try adding a street address.";
                else friendly = "Something went wrong reading your text. Try again in a moment.";
                setCreateLeadError(friendly);
                setCreateLeadLoading(false);
                setCreateLeadLoadingMessage(null);
                setCreateLeadLoadingSteps(null);
                return;
            }
            const extracted = parseRes.data.data;
            const addr = extracted.address || {};

            // Missing-street guard. The old all-fields-empty check let
            // city-only extractions like "Phoenix, AZ" slip through and
            // surface a misleading "Couldn't find Phoenix" autocomplete-miss
            // error. Now we fail explicitly on no-street with a message that
            // points at the real problem.
            if (!addr.streetAddress) {
                const partial = [addr.city, addr.state].filter(Boolean).join(', ');
                setCreateLeadError(
                    partial
                        ? `Got "${partial}" but no street address. Add the street, or use the Address tab.`
                        : 'No address detected in that text. Try the Address tab instead.'
                );
                setCreateLeadLoading(false);
                setCreateLeadLoadingMessage(null);
                setCreateLeadLoadingSteps(null);
                return;
            }

            const queryStr = [addr.streetAddress, addr.city, addr.state, addr.zipcode].filter(Boolean).join(', ');

            // Step 1 complete — show the LLM-resolved street as the detail
            // so the user sees real progress, not just a moving spinner.
            setCreateLeadLoadingSteps(buildPasteLoadingSteps('complete', 'active', 'pending', { read: queryStr }));

            const autoUrl = process.env.REACT_APP_REAPI_AUTOCOMPLETE_URL;
            const autoRes = await cloudFunctionV2(autoUrl, { address: queryStr });
            if (createLeadFlowTokenRef.current !== flowToken) return;
            if (autoRes.status !== 200 || !Array.isArray(autoRes.data) || autoRes.data.length === 0) {
                setCreateLeadError(`Couldn't find "${queryStr}" — double-check the spelling, or use the Address tab to search.`);
                setCreateLeadLoading(false);
                setCreateLeadLoadingMessage(null);
                setCreateLeadLoadingSteps(null);
                return;
            }
            const top = autoRes.data[0];
            // Parse path: stage a preview-card confirmation before opening
            // VerifyProperty so the user can spot a wrong-property match.
            await fetchReapiAndOpenVerify({
                label: top.address,
                id: top.id,
                countyId: top.fips,
                latitude: top.latitude,
                longitude: top.longitude,
                zipcode: top.zip
            }, extracted, flowToken, true, buildPasteLoadingSteps('complete', 'complete', 'active', {
                read: queryStr,
                find: top.address
            }));
        } catch (err) {
            if (createLeadFlowTokenRef.current !== flowToken) return;
            // httpsCallable surfaces err.code/err.message like "deadline-exceeded",
            // "internal", "unavailable" — log them, but never show raw to the
            // user. Map to friendly text by transport-style code.
            console.error('parseLeadText error:', err?.code, err?.message, err);
            const code = err?.code || '';
            let friendly;
            if (code === 'unauthenticated' || code === 'permission-denied') {
                friendly = "You're signed out — refresh and try again.";
            } else if (code === 'deadline-exceeded') {
                friendly = "That took too long. Try again or paste a shorter version.";
            } else if (code === 'unavailable' || code === 'internal') {
                friendly = "Couldn't reach the server. Check your connection and try again.";
            } else {
                friendly = "Something went wrong reading your text. Try again in a moment.";
            }
            setCreateLeadError(friendly);
            setCreateLeadLoading(false);
            setCreateLeadLoadingMessage(null);
            setCreateLeadLoadingSteps(null);
        }
    }, [createLeadText, companyId, fetchReapiAndOpenVerify]);

    // Fires synchronously inside the modal's onPaste handler — flips the
    // Continue button to disabled BEFORE the setTimeout-deferred parse
    // submit fires. Without this, the user could race-click Continue during
    // the ~1-render gap between paste and onSubmit, triggering a double
    // submission.
    const handleCreateLeadPasteStart = useCallback(() => {
        setCreateLeadLoading(true);
        // Initial step shape — step 1 active. handleSubmitCreateLeadText will
        // override on the next tick (short-circuit collapses step 1 to
        // complete immediately; parse path leaves it active until parseLeadText
        // resolves). Setting it here means the checklist paints synchronously
        // with the paste rather than after the next render.
        setCreateLeadLoadingSteps(buildPasteLoadingSteps('active', 'pending', 'pending'));
        setCreateLeadLoadingMessage(null);
        setCreateLeadError(null);
    }, []);

    const handleLeadVerifyBack = useCallback(() => {
        // Stage 2 → wherever the user came from. Parse-path users land back
        // on the preview card (one click forward to retry; data intact);
        // pick-path users land on the textarea (no preview to return to).
        // We use `createLeadExtracted !== null` as the parse-path proxy —
        // fetchReapiAndOpenVerify only stores extracted overrides on the
        // parse path.
        if (createLeadExtracted !== null) {
            setCreateLeadPreviewPending(true);
        } else {
            setCreateLeadVerifyProperty(null);
            setCreateLeadExtracted(null);
            setCreateLeadPreviewPending(false);
        }
    }, [createLeadExtracted]);

    // Preview card → VerifyProperty overlay. Drops the preview flag without
    // touching the loaded property data, so the overlay-render gate flips
    // and VerifyProperty mounts with the already-fetched REAPI doc.
    const handleConfirmCreateLeadPreview = useCallback(() => {
        setCreateLeadPreviewPending(false);
    }, []);

    // Preview card → textarea. Drops the loaded property + extracted data
    // but preserves the original text so the user can edit and re-submit
    // (e.g., add a state abbreviation if the address resolved wrong).
    const handleEditCreateLeadText = useCallback(() => {
        setCreateLeadVerifyProperty(null);
        setCreateLeadExtracted(null);
        setCreateLeadPreviewPending(false);
        setCreateLeadError(null);
        setCreateLeadLoadingSteps(null);
    }, []);

    // Prefill VerifyProperty's "Share link" image-URL input with the parser's
    // extracted cloud-storage folder. Strictly Drive / Dropbox / OneDrive
    // because the downstream calculateRepairCostFromImages opens this URL as
    // a FOLDER (expanded server-side). Direct image URLs and dealUrls are NOT
    // share-link compatible — they flow through the payload's `imageUrls`
    // bucket separately for the worker to handle, and they'd mislead the user
    // if shown here (a deal URL is a wholesaler landing page, not a photo
    // folder).
    const createLeadInitialImageUrl = useMemo(() => {
        if (!createLeadExtracted) return '';
        const folders = createLeadExtracted.imageFolders || {};
        return folders.drive?.[0]
            || folders.dropbox?.[0]
            || folders.oneDrive?.[0]
            || '';
    }, [createLeadExtracted]);

    // Provider classifier — buckets a URL into imageFolders.{drive,dropbox,oneDrive}
    // if it's a known cloud-storage host, else falls through to imageUrls.
    // Mirrors functions/utils/imageProviders → getImageFolderProvider on the
    // backend so the frontend and worker agree on routing.
    const classifyImageUrl = useCallback((url) => {
        if (typeof url !== 'string' || url.trim().length === 0) return { bucket: null };
        const u = url.trim().toLowerCase();
        if (u.includes('drive.google.com')) return { bucket: 'drive' };
        if (u.includes('dropbox.com')) return { bucket: 'dropbox' };
        if (u.includes('onedrive.live.com') || u.includes('1drv.ms') || u.includes('sharepoint.com')) return { bucket: 'oneDrive' };
        return { bucket: null }; // → imageUrls
    }, []);

    // Preview payload — passed to CreateLeadModal as the `preview` prop.
    // Reads canonical address + beds/baths from the REAPI fetch and notes/
    // sender from the parser output. Memoised so the modal doesn't see a
    // fresh object every render while preview is open.
    //
    // Canonical address: REAPI usually returns `.label` formatted as
    // "STREET, CITY, STATE ZIP", which is what we want to display. Fall
    // back to constructing from the address parts if the label is missing
    // — better to show a partial address than render an empty heading.
    const createLeadPreview = useMemo(() => {
        if (!createLeadPreviewPending || !createLeadVerifyProperty) return null;
        const info = createLeadVerifyProperty.propertyInfo || {};
        const addr = info.address || {};
        let canonicalLabel = addr.label || '';
        if (!canonicalLabel) {
            const street = addr.address || addr.streetAddress || '';
            const cityStateZip = [addr.city, addr.state, addr.zip || addr.zipcode]
                .filter(Boolean)
                .join(' ')
                .trim();
            canonicalLabel = [street, cityStateZip].filter(Boolean).join(', ');
        }
        return {
            canonicalLabel,
            bedrooms: typeof info.bedrooms === 'number' ? info.bedrooms : null,
            bathrooms: typeof info.bathrooms === 'number' ? info.bathrooms : null,
            notes: createLeadExtracted?.notes || null,
            sender: createLeadExtracted?.sender || null
        };
    }, [createLeadPreviewPending, createLeadVerifyProperty, createLeadExtracted]);

    // ==================== Edit Address Modal Handlers ====================

    const openEditAddressModal = useCallback((leadId, currentAddress) => {
        setEditAddressLeadId(leadId);
        setEditAddressCurrentAddress(currentAddress);
        setEditAddressModalOpen(true);
        setEditAddressError(null);
        resetEditAddress();
        setEditAddressSelected(false);
        setEditAddressSelectedLabel('');
        setEditAddressStreet('');
        setEditAddressCity('');
        setEditAddressState('');
        setEditAddressZipcode('');
    }, [resetEditAddress]);

    const closeEditAddressModal = useCallback(() => {
        if (editAddressLoading) return;
        setEditAddressModalOpen(false);
        setEditAddressLeadId(null);
        setEditAddressCurrentAddress(null);
        setEditAddressError(null);
        resetEditAddress();
        setEditAddressSelected(false);
        setEditAddressSelectedLabel('');
        setEditAddressStreet('');
        setEditAddressCity('');
        setEditAddressState('');
        setEditAddressZipcode('');
    }, [editAddressLoading, resetEditAddress]);

    const handleEditAddressSearchChange = useCallback((e) => {
        setEditAddressQuery(e.target.value);
    }, [setEditAddressQuery]);

    const handleSelectEditAddress = useCallback((item) => {
        const label = item.label;
        const zip = item.zipcode || '';

        // Strip zip from end of label, then parse "Street, City, ST"
        let cleanedLabel = label;
        if (zip && cleanedLabel.endsWith(zip)) {
            cleanedLabel = cleanedLabel.slice(0, -zip.length).trim();
            if (cleanedLabel.endsWith(',')) {
                cleanedLabel = cleanedLabel.slice(0, -1).trim();
            }
        }

        const parts = cleanedLabel.split(', ');
        const state = parts.length >= 3 ? parts[parts.length - 1] : '';
        const city = parts.length >= 3 ? parts[parts.length - 2] : (parts.length >= 2 ? parts[parts.length - 1] : '');
        const street = parts.slice(0, Math.max(1, parts.length - 2)).join(', ');

        setEditAddressSelectedLabel(label);
        setEditAddressStreet(street);
        setEditAddressCity(city);
        setEditAddressState(state);
        setEditAddressZipcode(zip);
        setEditAddressSelected(true);
        resetEditAddress();
    }, [resetEditAddress]);

    const handleClearEditAddress = useCallback(() => {
        resetEditAddress();
        setEditAddressSelected(false);
        setEditAddressSelectedLabel('');
        setEditAddressStreet('');
        setEditAddressCity('');
        setEditAddressState('');
        setEditAddressZipcode('');
    }, [resetEditAddress]);

    const submitEditAddress = useCallback(async () => {
        if (!editAddressSelected || !editAddressLeadId || !companyId) return;

        setEditAddressLoading(true);
        setEditAddressError(null);

        try {
            const functions = getFunctions();
            const reprocessLeadFn = httpsCallable(functions, 'reprocessLead');
            const response = await reprocessLeadFn({
                leadId: editAddressLeadId,
                streetAddress: editAddressStreet.trim(),
                city: editAddressCity.trim(),
                state: editAddressState.trim(),
                zipcode: editAddressZipcode.trim()
            });

            if (response.data?.status === 200) {
                // Update local state: reset the lead to queued with new address
                setLeads(prev => prev.map(lead => {
                    if (lead.leadId === editAddressLeadId) {
                        return {
                            ...lead,
                            propertyId: null,
                            status: 'queued',
                            address: {
                                streetAddress: editAddressStreet.trim(),
                                city: editAddressCity.trim(),
                                state: editAddressState.trim(),
                                zipcode: editAddressZipcode.trim(),
                                label: editAddressSelectedLabel
                            }
                        };
                    }
                    return lead;
                }));
                setEditAddressModalOpen(false);
                setEditAddressLeadId(null);
                setEditAddressCurrentAddress(null);
            } else {
                setEditAddressError(response.data?.error || 'Failed to reprocess lead');
            }
        } catch (error) {
            console.error('Error reprocessing lead:', error);
            setEditAddressError(error.message || 'An error occurred');
        } finally {
            setEditAddressLoading(false);
        }
    }, [companyId, editAddressLeadId, editAddressSelected, editAddressStreet,
        editAddressCity, editAddressState, editAddressZipcode, editAddressSelectedLabel]);

    // ==================== End Edit Address Modal Handlers ====================

    // Stage 2 confirm — builds the parser-shaped payload from the
    // user-corrected fields and calls createLeadManual, then attaches the
    // existing placeholder-doc watcher so the row materialises once
    // enrichment completes.
    //
    // `params` carries fields that propertyDataReAPI.property() drops on the
    // floor (propertyType in particular) — read from it for anything missing
    // off defaultPropertyData rather than letting the worker's OpenAI
    // re-extract guess (it can't infer "Multi Family" from beds/baths alone).
    const handleLeadComplete = useCallback(async ({ params, defaultPropertyData, propertyImages }) => {
        if (!companyId) return;
        if (createLeadSubmittingRef.current) return; // ignore double-press while a submit is in flight
        createLeadSubmittingRef.current = true;
        setCreateLeadLoading(true);
        setCreateLeadError(null);
        // Scheduler for the failure toast. Uses a dedicated state slot so it
        // can never overwrite a different lead's in-flight processingToast.
        // Retries can fire it back-to-back; clear any prior timer so the
        // second toast gets a full 10s window instead of inheriting the
        // first's stale dismiss.
        const showFailedToast = (message) => {
            if (createLeadFailedToastTimerRef.current) {
                clearTimeout(createLeadFailedToastTimerRef.current);
            }
            setCreateLeadFailedToast({ message });
            createLeadFailedToastTimerRef.current = setTimeout(() => {
                setCreateLeadFailedToast(null);
                createLeadFailedToastTimerRef.current = null;
            }, 10000);
        };
        try {
            const p = defaultPropertyData || {};
            const addr = p.address || {};
            const payload = {
                address: {
                    streetAddress: addr.streetAddress || addr.address || '',
                    city: addr.city || '',
                    state: addr.state || '',
                    zipcode: addr.zip || addr.zipcode || ''
                }
            };
            if (typeof p.bedrooms === 'number') payload.bedrooms = p.bedrooms;
            if (typeof p.bathrooms === 'number') payload.bathrooms = p.bathrooms;
            if (typeof p.livingSquareFeet === 'number') payload.squareFeet = p.livingSquareFeet;
            if (typeof p.yearBuilt === 'number') payload.yearBuilt = p.yearBuilt;
            // propertyType lives on params (from REAPI), not on
            // propertyDataReAPI.property's output. Falls back to whatever the
            // parser inferred from the original paste.
            const propertyType = params?.propertyType || createLeadExtracted?.propertyType || null;
            if (propertyType) payload.propertyType = propertyType;
            if (p.adu === true) payload.adu = true;
            if (p.pool === true) payload.pool = true;
            if (p.garage === true) {
                payload.garage = true;
                if (typeof p.garageSpaces === 'number') payload.garageSpaces = p.garageSpaces;
                if (p.garageType) payload.garageType = p.garageType;
            }
            if (p.basement === true) {
                payload.basement = true;
                if (typeof p.basementSqFootage === 'number') payload.basementSqFootage = p.basementSqFootage;
            }
            if (p.propertyNotes) payload.notes = p.propertyNotes;
            // remodelLevel is optional in leadMode (the insights-view condition
            // picker isn't required to submit), but if the user picked one,
            // persist it via customFields so the choice isn't silently
            // discarded. The parser schema's customFields is a free-form bag
            // preserved through the worker onto the lead doc.
            if (typeof p.remodelLevel === 'number') {
                payload.customFields = { ...(payload.customFields || {}), remodelLevel: p.remodelLevel };
            }
            // Parser-extracted sender + asking price carried through — VerifyProperty
            // doesn't capture these fields directly.
            const extracted = createLeadExtracted || {};
            if (extracted.sender && (extracted.sender.email || extracted.sender.phone || extracted.sender.name)) {
                payload.sender = extracted.sender;
            }
            if (typeof extracted.price === 'number' && extracted.price > 0) {
                payload.price = extracted.price;
            }
            // Image handoff — start with whatever the parser extracted
            // (preserves multi-folder / multi-image pastes), then layer the
            // user-edited URL from VerifyProperty's input on top. If the
            // user's URL is identical to what we extracted, no-op; if
            // different, route to the right bucket so the worker downloads
            // from the correct provider.
            const baseFolders = extracted.imageFolders || {};
            const folders = {
                drive: Array.isArray(baseFolders.drive) ? [...baseFolders.drive] : [],
                dropbox: Array.isArray(baseFolders.dropbox) ? [...baseFolders.dropbox] : [],
                oneDrive: Array.isArray(baseFolders.oneDrive) ? [...baseFolders.oneDrive] : []
            };
            const directUrls = Array.isArray(extracted.imageUrls) ? [...extracted.imageUrls] : [];
            const editedUrl = typeof propertyImages === 'string' ? propertyImages.trim() : '';
            if (editedUrl) {
                const { bucket } = classifyImageUrl(editedUrl);
                const alreadyPresent =
                    folders.drive.includes(editedUrl) ||
                    folders.dropbox.includes(editedUrl) ||
                    folders.oneDrive.includes(editedUrl) ||
                    directUrls.includes(editedUrl);
                if (!alreadyPresent) {
                    if (bucket) folders[bucket].push(editedUrl);
                    else directUrls.push(editedUrl);
                }
            }
            if (folders.drive.length || folders.dropbox.length || folders.oneDrive.length) {
                payload.imageFolders = {};
                if (folders.drive.length) payload.imageFolders.drive = folders.drive;
                if (folders.dropbox.length) payload.imageFolders.dropbox = folders.dropbox;
                if (folders.oneDrive.length) payload.imageFolders.oneDrive = folders.oneDrive;
            }
            if (directUrls.length) {
                payload.imageUrls = directUrls;
            }

            const fns = getFunctions();
            const createLeadManualFn = httpsCallable(fns, 'createLeadManual');
            const response = await createLeadManualFn({ data: payload });

            if (response.data?.status === 200) {
                const newLeadId = response.data?.data?.leadId;
                const submittedAddress = payload.address.streetAddress;

                recordEvent('lead_created', {
                    leadId: newLeadId || null,
                    source: createLeadExtracted ? 'paste' : 'verify',
                });

                setCreateLeadVerifyProperty(null);
                setCreateLeadExtracted(null);
                closeCreateLeadModal();
                // Clear any address search that was active when the user clicked
                // "+ Create lead for this address" — without this the search filter
                // stays in place, the still-processing lead doesn't match it, and
                // the empty-state button remains visible, inviting duplicate submissions.
                setAddressSearch('');
                updateSearchParamsRef.current({ search: null });
                handleRefresh();

                // Reuse the watcher pattern — surface the row once enrichment
                // completes (or the placeholder is deleted via merge). 90s
                // safety net mirrors the previous flow.
                if (newLeadId) {
                    const db = getFirestore();
                    const leadRef = doc(db, 'companies', companyId, 'leads', newLeadId);

                    // 'enriching' is the only in-flight stage we can observe: the
                    // worker flips status→'complete' (at saveLeadBatched, which drops
                    // processingStage) BEFORE it writes 'running_underwriting' /
                    // 'finalizing', and this watcher unsubscribes on 'complete'. So
                    // those later stages never reach us — anything else falls back to
                    // the address line.
                    const stageLabel = (stage) =>
                        stage === 'enriching' ? 'Enriching…' : `Processing ${submittedAddress}…`;

                    // Unsubscribe + drop the watcher. Leaves the toast untouched so
                    // a terminal state can swap it for a success/failure variant.
                    const teardown = () => {
                        const entry = createLeadWatchersRef.current.get(newLeadId);
                        if (!entry) return;
                        clearTimeout(entry.timeoutId);
                        entry.unsubscribe();
                        createLeadWatchersRef.current.delete(newLeadId);
                    };
                    const finish = () => {
                        teardown();
                        setProcessingToast(prev => (prev?.leadId === newLeadId ? null : prev));
                    };
                    // Terminal toast (success/failed): stop watching, show it, and
                    // auto-dismiss after a beat. Re-register the dismiss timer in the
                    // watcher map so the unmount cleanup still clears it.
                    const settleToast = (toast) => {
                        teardown();
                        setProcessingToast({ leadId: newLeadId, ...toast });
                        const dismissId = setTimeout(() => {
                            setProcessingToast(prev => (prev?.leadId === newLeadId ? null : prev));
                            createLeadWatchersRef.current.delete(newLeadId);
                        }, 10000);
                        createLeadWatchersRef.current.set(newLeadId, { unsubscribe: () => {}, timeoutId: dismissId });
                    };

                    const timeoutId = setTimeout(finish, 90000);
                    const unsubscribe = onSnapshot(leadRef, (snap) => {
                        if (!snap.exists()) {
                            handleRefresh();
                            finish();
                            return;
                        }
                        const leadDoc = snap.data()?.data;
                        const status = leadDoc?.status;
                        if (status === 'complete') {
                            handleRefresh();
                            // propertyId is written atomically with status:'complete'
                            // (saveLeadBatched) and always resolves, so the link is safe.
                            settleToast({
                                variant: 'success',
                                message: `${submittedAddress} is ready`,
                                propertyId: leadDoc?.propertyId || null,
                            });
                            return;
                        }
                        if (status === 'failed') {
                            handleRefresh();
                            // No link — a failed lead may never have gotten a propertyId.
                            settleToast({
                                variant: 'failed',
                                message: `Couldn't process ${submittedAddress}`,
                            });
                            return;
                        }
                        // Still in flight — reflect the pipeline stage.
                        setProcessingToast(prev => (
                            prev?.leadId === newLeadId
                                ? { ...prev, message: stageLabel(leadDoc?.processingStage) }
                                : prev
                        ));
                    }, (err) => {
                        console.error('createLead watcher error:', err);
                        finish();
                    });
                    createLeadWatchersRef.current.set(newLeadId, { unsubscribe, timeoutId });
                    setProcessingToast({ leadId: newLeadId, variant: 'processing', message: `Processing ${submittedAddress}…` });
                }
            } else {
                // createLeadManual returned non-200. The CreateLeadModal is
                // hidden while the VerifyProperty overlay is open, so the
                // createLeadError prop would never reach the user. Surface a
                // failed-variant toast (same pattern enrichment failure uses)
                // and keep VerifyProperty open so they can correct + retry
                // without re-pasting. Log the raw backend error — never show it.
                const backendStatus = response.data?.status;
                const backendError = response.data?.error;
                if (backendError) console.warn('[createLeadManual] backend error:', backendStatus, backendError);
                const failAddress = payload.address.streetAddress || '';
                const failMessage = failAddress
                    ? `Couldn't create lead for ${failAddress}. Try again in a moment.`
                    : "Couldn't create that lead. Try again in a moment.";
                showFailedToast(failMessage);
            }
        } catch (err) {
            // httpsCallable transport failure — map by code to a friendly toast,
            // log the raw err. Same toast pattern as the non-200 branch above.
            console.error('createLeadManual error:', err?.code, err?.message, err);
            const code = err?.code || '';
            const failAddress = defaultPropertyData?.address?.streetAddress || defaultPropertyData?.address?.address || '';
            let failMessage;
            if (code === 'unauthenticated' || code === 'permission-denied') {
                failMessage = "You're signed out — refresh and try again.";
            } else if (code === 'deadline-exceeded') {
                failMessage = "That took too long. Try again in a moment.";
            } else if (code === 'unavailable' || code === 'internal') {
                failMessage = "Couldn't reach the server. Check your connection and try again.";
            } else {
                failMessage = failAddress
                    ? `Couldn't create lead for ${failAddress}. Try again in a moment.`
                    : "Couldn't create that lead. Try again in a moment.";
            }
            showFailedToast(failMessage);
        } finally {
            setCreateLeadLoading(false);
            createLeadSubmittingRef.current = false;
        }
    }, [companyId, createLeadExtracted, closeCreateLeadModal, handleRefresh, classifyImageUrl, setAddressSearch]);

    // Portfolio modal handlers
    const openPortfolioModal = useCallback(() => {
        setPortfolioModalOpen(true);
        setPortfolioError(null);
        setPortfolioName('');
        setPortfolioCombinedPrice('');
        setPortfolioSelectedLeadIds([]);
        setPortfolioSelectedLeadsData({});
        setPortfolioSearchTerm('');
        setDebouncedPortfolioSearch('');
        setPortfolioSearchResults(null);
        setPortfolioSearchLoading(false);
    }, []);

    const closePortfolioModal = useCallback(() => {
        if (portfolioLoading) return;
        setPortfolioModalOpen(false);
        setPortfolioLoading(false);
        setPortfolioError(null);
        setPortfolioName('');
        setPortfolioCombinedPrice('');
        setPortfolioSelectedLeadIds([]);
        setPortfolioSelectedLeadsData({});
        setPortfolioSearchTerm('');
        setDebouncedPortfolioSearch('');
        setPortfolioSearchResults(null);
        setPortfolioSearchLoading(false);
    }, [portfolioLoading]);

    const handleTogglePortfolioLead = useCallback((leadId, leadData) => {
        setPortfolioSelectedLeadIds(prev => {
            if (prev.includes(leadId)) {
                setPortfolioSelectedLeadsData(prevData => {
                    const next = { ...prevData };
                    delete next[leadId];
                    return next;
                });
                return prev.filter(id => id !== leadId);
            } else {
                if (leadData) {
                    setPortfolioSelectedLeadsData(prevData => ({
                        ...prevData,
                        [leadId]: leadData
                    }));
                }
                return [...prev, leadId];
            }
        });
    }, []);

    const submitCreatePortfolio = useCallback(async () => {
        if (portfolioLoading || portfolioSelectedLeadIds.length < 2 || !companyId) return;

        setPortfolioLoading(true);
        setPortfolioError(null);

        try {
            const functions = getFunctions();
            const managePortfolioFn = httpsCallable(functions, 'managePortfolio');
            const priceStr = String(portfolioCombinedPrice).replace(/[^0-9]/g, '');
            const response = await managePortfolioFn({
                action: 'create',
                name: portfolioName.trim() || null,
                combinedAskingPrice: priceStr ? parseInt(priceStr, 10) : null,
                leadIds: portfolioSelectedLeadIds
            });

            if (response.data?.status === 200) {
                closePortfolioModal();
                handleRefresh();
            } else {
                setPortfolioError(response.data?.error || 'Failed to create portfolio');
            }
        } catch (error) {
            console.error('Error creating portfolio:', error);
            setPortfolioError(error.message || 'An error occurred');
        } finally {
            setPortfolioLoading(false);
        }
    }, [companyId, portfolioName, portfolioCombinedPrice, portfolioSelectedLeadIds,
        closePortfolioModal, handleRefresh, portfolioLoading]);

    // Filtered autocomplete results for state/city (local filtering)
    const filteredStateResults = useMemo(() => {
        if (!filterStateName) return [];
        const lower = filterStateName.toLowerCase();
        return availableStates
            .filter(s => {
                const sLower = s.toLowerCase();
                if (sLower === lower) return false;
                if (sLower.startsWith(lower)) return true;
                // Cross-match: if stored as "TX", also match when user types "tex"
                // and vice versa — if stored as "Texas", match when user types "tx"
                const fullName = STATE_ABBR_TO_NAME[sLower];
                const abbr = STATE_NAME_TO_ABBR[sLower];
                if (fullName && fullName.startsWith(lower)) return true;
                if (abbr && abbr.startsWith(lower)) return true;
                return false;
            })
            .map(s => ({ label: s }));
    }, [filterStateName, availableStates]);

    const filteredCityResults = useMemo(() => {
        if (!filterCity) return [];
        const lower = filterCity.toLowerCase();
        return availableCities
            .filter(c => c.toLowerCase().startsWith(lower) && c.toLowerCase() !== lower)
            .map(c => ({ label: c }));
    }, [filterCity, availableCities]);

    const handleSelectFilterState = useCallback((item) => {
        setFilterStateName(item.label);
    }, []);

    const handleSelectFilterCity = useCallback((item) => {
        setFilterCity(item.label);
    }, []);

    const handleClearFilterState = useCallback(() => {
        setFilterStateName('');
    }, []);

    const handleClearFilterCity = useCallback(() => {
        setFilterCity('');
    }, []);

    const handleToggleFilterPropertyType = useCallback((rawValue) => {
        setFilterPropertyType(prev =>
            prev.includes(rawValue) ? prev.filter(v => v !== rawValue) : [...prev, rawValue]
        );
    }, []);

    const handleToggleFilterAssignee = useCallback((userId) => {
        setFilterAssignees(prev =>
            prev.includes(userId) ? prev.filter(v => v !== userId) : [...prev, userId]
        );
    }, []);

    const handleToggleFilterDealStatus = useCallback((statusValue) => {
        setFilterDealStatuses(prev =>
            prev.includes(statusValue) ? prev.filter(v => v !== statusValue) : [...prev, statusValue]
        );
    }, []);

    const handleToggleFilterPortfolio = useCallback((portfolioId) => {
        setFilterPortfolios(prev =>
            prev.includes(portfolioId) ? prev.filter(v => v !== portfolioId) : [...prev, portfolioId]
        );
    }, []);

    const handleToggleFilterAuctionStatus = useCallback((statusValue) => {
        setFilterAuctionStatus(prev =>
            prev.includes(statusValue) ? prev.filter(v => v !== statusValue) : [...prev, statusValue]
        );
    }, []);

    // Unlike its auction/lien siblings this reads the current value rather than using the
    // functional form, because dropping the bridge path has to take the deal-score draft with it.
    // The score exists only on that path and a range filter excludes nulls, so leaving a typed
    // figure behind would apply an invisible filter that empties the table (the modal hides the
    // input in that state, so the user could not even see what was still set).
    const handleToggleFilterFinancingPath = useCallback((pathValue) => {
        const next = filterFinancingPaths.includes(pathValue)
            ? filterFinancingPaths.filter(v => v !== pathValue)
            : [...filterFinancingPaths, pathValue];
        setFilterFinancingPaths(next);
        if (next.length > 0 && !next.includes('bridgeRefi')) setFilterDealScoreMin('');
    }, [filterFinancingPaths]);

    const handleToggleFilterLienPosition = useCallback((positionValue) => {
        setFilterLienPosition(prev =>
            prev.includes(positionValue) ? prev.filter(v => v !== positionValue) : [...prev, positionValue]
        );
    }, []);

    // Filter modal handlers
    const openFilterModal = useCallback(() => {
        setFilterPriceMin(appliedFilters.priceMin ? numberFormatter.format(parseInt(appliedFilters.priceMin, 10)) : '');
        setFilterPriceMax(appliedFilters.priceMax ? numberFormatter.format(parseInt(appliedFilters.priceMax, 10)) : '');
        setFilterStateName(appliedFilters.state || '');
        setFilterCity(appliedFilters.city || '');
        setFilterBedsMin(appliedFilters.bedsMin || '');
        setFilterBedsMax(appliedFilters.bedsMax || '');
        setFilterBathsMin(appliedFilters.bathsMin || '');
        setFilterBathsMax(appliedFilters.bathsMax || '');
        setFilterSqftMin(appliedFilters.sqftMin || '');
        setFilterSqftMax(appliedFilters.sqftMax || '');
        setFilterYearBuiltMin(appliedFilters.yearBuiltMin || '');
        setFilterYearBuiltMax(appliedFilters.yearBuiltMax || '');
        setFilterSpreadMin(appliedFilters.spreadMin || '');
        setFilterMaoVsAskingMax(appliedFilters.maoVsAskingMax || '');
        setFilterFinancingPaths(resolveFinancingPathFilter(appliedFilters));
        setFilterDealScoreMin(appliedFilters.dealScoreMin || '');
        setFilterDateMin(appliedFilters.dateMin || '');
        setFilterDateMax(appliedFilters.dateMax || '');
        setFilterPropertyType(Array.isArray(appliedFilters.propertyType) ? appliedFilters.propertyType : []);
        setFilterAssignees(Array.isArray(appliedFilters.assignees) ? appliedFilters.assignees : []);
        setFilterDealStatuses(Array.isArray(appliedFilters.dealStatuses) ? appliedFilters.dealStatuses : []);
        setFilterAuctionStatus(Array.isArray(appliedFilters.auctionStatus) ? appliedFilters.auctionStatus : []);
        setFilterLienPosition(Array.isArray(appliedFilters.lienPosition) ? appliedFilters.lienPosition : []);
        setFilterAuctionDateMin(appliedFilters.auctionDateMin || '');
        setFilterAuctionDateMax(appliedFilters.auctionDateMax || '');
        setFilterSenders(Array.isArray(appliedFilters.senders) ? appliedFilters.senders : []);
        setFilterSenderSearch('');
        setFilterPortfolios(Array.isArray(appliedFilters.portfolios) ? appliedFilters.portfolios : []);
        // Archived isn't part of appliedFilters — it's a top-level mode flag.
        // Seed the draft from the live state so the toggle reflects reality.
        setFilterArchived(archivedFilter === true);
        setFilterModalOpen(true);
    }, [appliedFilters, archivedFilter]);

    const closeFilterModal = useCallback(() => {
        setFilterModalOpen(false);
    }, []);

    const clearAllFilters = useCallback(() => {
        setFilterPriceMin('');
        setFilterPriceMax('');
        setFilterStateName('');
        setFilterCity('');
        setFilterBedsMin('');
        setFilterBedsMax('');
        setFilterBathsMin('');
        setFilterBathsMax('');
        setFilterSqftMin('');
        setFilterSqftMax('');
        setFilterYearBuiltMin('');
        setFilterYearBuiltMax('');
        setFilterSpreadMin('');
        setFilterMaoVsAskingMax('');
        setFilterFinancingPaths([]);
        setFilterDealScoreMin('');
        setFilterDateMin('');
        setFilterDateMax('');
        setFilterPropertyType([]);
        setFilterAssignees([]);
        setFilterDealStatuses([]);
        setFilterAuctionStatus([]);
        setFilterLienPosition([]);
        setFilterAuctionDateMin('');
        setFilterAuctionDateMax('');
        setFilterSenders([]);
        setFilterSenderSearch('');
        setFilterPortfolios([]);
        setFilterArchived(false);
    }, []);

    const clearAllFiltersAndClose = useCallback(() => {
        clearAllFilters();
        setAppliedFilters({});
        setArchivedFilter(false);
        updateSearchParams({ filters: null, archived: null, page: '1' });
        setFilterModalOpen(false);
    }, [clearAllFilters, updateSearchParams]);

    // Remove a single filter (or a range pair) from appliedFilters and the
    // URL. Used by the active-filter chip × buttons under the toolbar.
    const removeAppliedFilterKeys = useCallback((keys) => {
        setAppliedFilters((prev) => {
            const next = { ...prev };
            keys.forEach((k) => { delete next[k]; });
            const filtersParam = Object.keys(next).length > 0 ? JSON.stringify(next) : null;
            updateSearchParams({ filters: filtersParam, page: '1' });
            return next;
        });
    }, [updateSearchParams]);

    const applyFilters = useCallback(() => {
        const filters = {};
        if (filterPriceMin) filters.priceMin = String(filterPriceMin).replace(/[^0-9]/g, '');
        if (filterPriceMax) filters.priceMax = String(filterPriceMax).replace(/[^0-9]/g, '');
        if (filterStateName) filters.state = filterStateName;
        if (filterCity) filters.city = filterCity;
        if (filterBedsMin) filters.bedsMin = filterBedsMin;
        if (filterBedsMax) filters.bedsMax = filterBedsMax;
        if (filterBathsMin) filters.bathsMin = filterBathsMin;
        if (filterBathsMax) filters.bathsMax = filterBathsMax;
        if (filterSqftMin) filters.sqftMin = filterSqftMin;
        if (filterSqftMax) filters.sqftMax = filterSqftMax;
        if (filterYearBuiltMin) filters.yearBuiltMin = filterYearBuiltMin;
        if (filterYearBuiltMax) filters.yearBuiltMax = filterYearBuiltMax;
        if (filterSpreadMin) filters.spreadMin = filterSpreadMin;
        if (filterMaoVsAskingMax) filters.maoVsAskingMax = filterMaoVsAskingMax;
        // Written as the array only — never alongside the legacy `bridgeRefiOnly` boolean, so the
        // two shapes can't both be live in one dict and disagree.
        if (filterFinancingPaths.length > 0) filters.financingPaths = filterFinancingPaths;
        if (filterDealScoreMin) filters.dealScoreMin = filterDealScoreMin;
        if (filterDateMin) filters.dateMin = filterDateMin;
        if (filterDateMax) filters.dateMax = filterDateMax;
        if (filterPropertyType.length > 0) filters.propertyType = filterPropertyType;
        if (filterAssignees.length > 0) filters.assignees = filterAssignees;
        if (filterDealStatuses.length > 0) filters.dealStatuses = filterDealStatuses;
        if (filterAuctionStatus.length > 0) filters.auctionStatus = filterAuctionStatus;
        if (filterLienPosition.length > 0) filters.lienPosition = filterLienPosition;
        if (filterAuctionDateMin) filters.auctionDateMin = filterAuctionDateMin;
        if (filterAuctionDateMax) filters.auctionDateMax = filterAuctionDateMax;
        if (filterSenders.length > 0) filters.senders = filterSenders;
        if (filterPortfolios.length > 0) filters.portfolios = filterPortfolios;

        setAppliedFilters(filters);
        const filtersParam = Object.keys(filters).length > 0 ? JSON.stringify(filters) : null;

        // Archived is orthogonal to the in-modal filter dictionary — it's a
        // top-level mode flag. Flip the live state and the URL together so a
        // reload or shared link comes back into the same view.
        setArchivedFilter(filterArchived);
        // When the user opts INTO archived, the lead-type/source/automation
        // chips in the page header would be misleading (archived view ignores
        // them — buildLeadsParams returns leadType=null when archived is true).
        // Clear them so the header truthfully reflects the active query.
        const archivedSearchParams = filterArchived
            ? { filters: filtersParam, page: '1', archived: '1', filter: null, sources: null, senders: null }
            : { filters: filtersParam, page: '1', archived: null };
        if (filterArchived) {
            setLeadTypeFilter(null);
            setSelectedSources([]);
        }
        updateSearchParams(archivedSearchParams);
        setFilterModalOpen(false);
    }, [filterPriceMin, filterPriceMax, filterStateName, filterCity, filterBedsMin, filterBedsMax,
        filterBathsMin, filterBathsMax, filterSqftMin, filterSqftMax, filterYearBuiltMin, filterYearBuiltMax,
        filterSpreadMin, filterMaoVsAskingMax, filterFinancingPaths, filterDealScoreMin,
        filterDateMin, filterDateMax, filterPropertyType,
        filterAssignees, filterDealStatuses, filterAuctionStatus, filterLienPosition,
        filterAuctionDateMin, filterAuctionDateMax, filterSenders, filterPortfolios, filterArchived, updateSearchParams]);

    // Full-page spinner is for the genuine initial load only. Gate it on
    // `tableLoading` (the page query, which runs in BOTH views) rather than the
    // view-dependent `leadsLoading`. Using `leadsLoading` meant that switching
    // to map view tore the whole page down the moment the bounds query fired its
    // first fetch — the map mounts, idles, sets bounds, the query goes
    // `isLoading`, and `<Loading />` unmounted the map mid-flight (white-screen
    // flash + map remount churn). Map view has its own inline loading affordances.
    const isLoading = loading || companyLoading || tableLoading || unipileAccountsLoading;

    if (isLoading) return <Loading />;

    return (
        <div className={`leads-outer-container dashboard-page-outer-container leads-app-page ${railOpen ? 'leads-outer-container-with-rail' : ''}`}>
                <div className="leads-workspace">
                <div className="leads-inner-container">
                    <div className="dashboard-page-header">
                        <div className="leads-title-container">
                            <h1 className="heading-large-bold colour-primary">
                                Leads
                            </h1>
                            <InProgressLeadsBadge />
                            <button
                                type="button"
                                className="leads-activity-rail-icon-toggle mobile-none"
                                onClick={toggleRail}
                                aria-label={railOpen ? 'Hide activity panel' : 'Show activity panel'}
                                title={railOpen ? 'Hide activity' : 'Show activity'}
                                aria-pressed={railOpen}
                            >
                                <HugeiconsIcon icon={SidebarRightIcon} color={railOpen ? colour.gray07 : colour.gray05} size={18} />
                            </button>
                        </div>
                        <div className="light-grey-divider"></div>
                    </div>
                    <div className={`leads-body-container${isMobile && !tableView ? ' leads-body-map' : ''}`}>
                        {showLeadTypeChips && !showWaitingState && !showProcessingState && (
                            <LeadTypeTabs
                                value={leadTypeFilter}
                                onChange={handleFilterChange}
                            />
                        )}
                        {!showWaitingState && !showProcessingState && (
                        <div className="leads-toolbar">
                            {/*
                            Left cluster — what dataset / what view. Search
                            (primary daily action) + Table/Map segmented
                            switch are grouped together because they both
                            scope "the data I'm looking at right now."
                            */}
                            <div className="leads-toolbar-left">
                                {showSearchField ? (
                                    <div className="leads-search-container">
                                        <input
                                            ref={searchInputRef}
                                            type="search"
                                            className="leads-search-input body-regular colour-primary"
                                            placeholder="Search by address…"
                                            value={addressSearch}
                                            onChange={onChangeAddressSearch}
                                            aria-label="Search leads by address"
                                        />
                                        {(addressSearch || (isMobile && searchOpen)) && (
                                            <button
                                                type="button"
                                                className="leads-search-clear"
                                                onClick={isMobile ? handleSearchClearOrClose : clearAddressSearch}
                                                aria-label={addressSearch ? 'Clear search' : 'Close search'}
                                            >
                                                ×
                                            </button>
                                        )}
                                    </div>
                                ) : (
                                    <button
                                        type="button"
                                        className="leads-search-icon-button"
                                        onClick={openMobileSearch}
                                        aria-label="Search leads by address"
                                        aria-expanded="false"
                                    >
                                        <HugeiconsIcon icon={Search01Icon} color={colour.gray06} size={18} />
                                    </button>
                                )}
                                <div className={`leads-view-segmented-toggle leads-hide-mobile${isRefetching ? ' leads-view-segmented-toggle-loading' : ''}`} role="tablist" aria-label="Leads view" aria-busy={isRefetching}>
                                    <button
                                        type="button"
                                        role="tab"
                                        aria-selected={tableView}
                                        className={`leads-view-segmented-option body-semibold ${tableView ? 'leads-view-segmented-option-active' : ''}`}
                                        onClick={() => handleViewToggle(true)}
                                        disabled={tableView}
                                    >
                                        <HugeiconsIcon icon={LeftToRightListBulletIcon} color={tableView ? colour.gray07 : colour.gray05} size={14} />
                                        <span>Table</span>
                                    </button>
                                    <button
                                        type="button"
                                        role="tab"
                                        aria-selected={!tableView}
                                        className={`leads-view-segmented-option body-semibold ${!tableView ? 'leads-view-segmented-option-active' : ''}`}
                                        onClick={() => handleViewToggle(false)}
                                        disabled={!tableView}
                                    >
                                        <HugeiconsIcon icon={Location01Icon} color={!tableView ? colour.gray07 : colour.gray05} size={14} />
                                        <span>Map</span>
                                    </button>
                                </div>
                            </div>
                            {activeFilterCount > 0 && (() => {
                                const chips = buildFilterChips(appliedFilters, dealStatusOptions, availablePortfolios);
                                if (chips.length === 0) return null;
                                return (
                                    <div className="leads-toolbar-chips" aria-label="Active filters">
                                        {chips.map((chip) => (
                                            <button
                                                key={chip.key}
                                                type="button"
                                                className="leads-active-filter-chip body-regular"
                                                onClick={() => removeAppliedFilterKeys(chip.removeKeys)}
                                                aria-label={`Remove filter ${chip.label}`}
                                            >
                                                <span>{chip.label}</span>
                                                <HugeiconsIcon icon={Cancel01Icon} color={colour.gray06} size={12} />
                                            </button>
                                        ))}
                                        {chips.length >= 2 && (
                                            <button
                                                type="button"
                                                className="leads-active-filters-clear body-regular colour-secondary"
                                                onClick={clearAllFiltersAndClose}
                                            >
                                                Clear all
                                            </button>
                                        )}
                                    </div>
                                );
                            })()}
                            {/*
                            Right cluster — tertiary view customization
                            (Filters, Columns) followed by the labeled
                            primary CTA (New lead). Eye terminates on the
                            solid dark button per data-table UX consensus.
                            */}
                            <div className="leads-toolbar-right">
                                <div className="leads-toolbar-secondary">
                                    <button
                                        type="button"
                                        className="leads-secondary-button body-regular"
                                        onClick={openFilterModal}
                                        disabled={!companyId || companyLoading}
                                    >
                                        <HugeiconsIcon icon={FilterHorizontalIcon} color={activeFilterCount > 0 ? colour.blue02 : colour.gray05} size={14} />
                                        <span className={activeFilterCount > 0 ? 'colour-primary' : 'colour-secondary'}>
                                            {activeFilterCount > 0 ? `Filters · ${activeFilterCount}` : 'Filters'}
                                        </span>
                                    </button>
                                    {tableView && (
                                        <span className="leads-hide-mobile">
                                            <ColumnToggle
                                                columns={buildColumnConfig()}
                                                onToggle={handleColumnToggle}
                                                onReorder={handleColumnReorder}
                                            />
                                        </span>
                                    )}
                                </div>
                                <div className="leads-create-menu-wrapper leads-hide-mobile">
                                    <button
                                        type="button"
                                        className="leads-create-primary-button body-bold"
                                        disabled={!companyId || companyLoading}
                                        onClick={() => {
                                            if (!createMenuOpen) recordEvent('create_lead_modal_opened', {});
                                            setCreateMenuOpen((prev) => !prev);
                                        }}
                                        aria-haspopup="menu"
                                        aria-expanded={createMenuOpen}
                                    >
                                        <HugeiconsIcon icon={PlusSignIcon} color={colour.gray00} size={14} />
                                        <span>New lead</span>
                                        <HugeiconsIcon icon={ArrowDown01Icon} color={colour.gray00} size={12} />
                                    </button>
                                    {createMenuOpen && (
                                        <>
                                            <ExitScreen clickScreen={() => setCreateMenuOpen(false)} />
                                            <div className="leads-create-menu bg-colour-white">
                                                <button
                                                    type="button"
                                                    className="leads-create-menu-item body-regular colour-primary"
                                                    onClick={() => {
                                                        setCreateMenuOpen(false);
                                                        openCreateLeadModal();
                                                    }}
                                                >
                                                    Create Lead
                                                </button>
                                                <button
                                                    type="button"
                                                    className="leads-create-menu-item body-regular colour-primary"
                                                    disabled={leads.length < 2}
                                                    onClick={() => {
                                                        setCreateMenuOpen(false);
                                                        openPortfolioModal();
                                                    }}
                                                >
                                                    Create Portfolio
                                                </button>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                        )}
                        {(showWaitingState || showProcessingState) && activeFilterCount > 0 && (() => {
                            const chips = buildFilterChips(appliedFilters, dealStatusOptions, availablePortfolios);
                            if (chips.length === 0) return null;
                            return (
                                <div className="leads-active-filters-row" aria-label="Active filters">
                                    {chips.map((chip) => (
                                        <button
                                            key={chip.key}
                                            type="button"
                                            className="leads-active-filter-chip body-regular"
                                            onClick={() => removeAppliedFilterKeys(chip.removeKeys)}
                                            aria-label={`Remove filter ${chip.label}`}
                                        >
                                            <span>{chip.label}</span>
                                            <HugeiconsIcon icon={Cancel01Icon} color={colour.gray06} size={12} />
                                        </button>
                                    ))}
                                    {chips.length >= 2 && (
                                        <button
                                            type="button"
                                            className="leads-active-filters-clear body-regular colour-secondary"
                                            onClick={clearAllFiltersAndClose}
                                        >
                                            Clear all
                                        </button>
                                    )}
                                </div>
                            );
                        })()}
                        {showProcessingState ? (
                            <div className="leads-empty-state">
                                <LeadsProcessingState
                                    lead={null}
                                    message={bootstrapProcessingMessage}
                                />
                            </div>
                        ) : showWaitingState ? (
                            <div className="leads-empty-state">
                                <LeadsWaitingState
                                    unipileAccounts={unipileAccounts}
                                    allowedFromNumbers={companyData?.smsConfig?.allowedFromNumbers}
                                    userPhone={userData?.phoneNumber}
                                    supplementaryText={bootstrapNoneMessage}
                                    onCreateLead={openCreateLeadModal}
                                />
                            </div>
                        ) : tableData.length === 0 && (tableView || !viewportFilterActive) ? (
                            <div className="leads-empty-state">
                                <span className="body-regular colour-secondary">
                                    {archivedFilter
                                        ? 'No archived leads'
                                        : debouncedSearch
                                        ? `No leads found matching "${debouncedSearch}"`
                                        : appliedFilters.assignees?.length > 0
                                            ? 'No leads found for the selected assignees'
                                            : activeFilterCount > 0
                                                ? 'No leads match your current filters'
                                                : leadTypeFilter === 'direct_to_seller'
                                                    ? 'No Direct to Seller leads yet'
                                                    : leadTypeFilter === 'property_lead'
                                                        ? 'No Property Leads yet'
                                                        : 'No leads found'}
                                </span>
                                {debouncedSearch && !archivedFilter && (
                                    <button
                                        type="button"
                                        className="text-button label-bold colour-primary"
                                        disabled={!companyId || companyLoading}
                                        onClick={() => {
                                            recordEvent('create_lead_modal_opened', {});
                                            openCreateLeadModal(debouncedSearch);
                                        }}
                                    >
                                        + Create lead for this address
                                    </button>
                                )}
                            </div>
                        ) : tableView && isMobile ? (
                            <>
                                {/* Mobile table view: a single full-width column of the
                                    same LeadCards (SpencersDeals list pattern), window-
                                    scrolled and virtualized. Rows are absolutely positioned
                                    in a spacer sized to the full list; translateY subtracts
                                    the virtualizer's scrollMargin (the list's document-top
                                    offset) so window-space starts map back into the spacer. */}
                                <div
                                    ref={tableCardsListRef}
                                    className="leads-table-cards"
                                    style={{ height: tableCardsVirtualizer.getTotalSize(), position: 'relative', width: '100%' }}
                                >
                                    {tableCardsVirtualizer.getVirtualItems().map((vItem) => {
                                        const lead = tableData[vItem.index];
                                        if (!lead) return null;
                                        return (
                                            <div
                                                key={vItem.key}
                                                data-index={vItem.index}
                                                ref={tableCardsVirtualizer.measureElement}
                                                className="leads-rail-vrow"
                                                style={{
                                                    position: 'absolute',
                                                    top: 0,
                                                    left: 0,
                                                    width: '100%',
                                                    transform: `translateY(${vItem.start - (tableCardsVirtualizer.options.scrollMargin || 0)}px)`,
                                                }}
                                            >
                                                <LeadCard
                                                    lead={lead}
                                                    isClicked={false}
                                                    onClick={handleLeadClick}
                                                    onOpenImages={handleOpenImages}
                                                    navigateOnImageClick={isMobile}
                                                />
                                            </div>
                                        );
                                    })}
                                </div>
                                {leadsTotal > PAGE_SIZE && (
                                    <Pagination
                                        total={leadsTotal}
                                        limit={PAGE_SIZE}
                                        currentPage={page}
                                        onPageChange={onPageChange}
                                        siblingCount={1}
                                    />
                                )}
                            </>
                        ) : tableView ? (
                            <div className={highlightFirstRow ? 'leads-first-row-highlight' : undefined}>
                                <LeadsTable
                                    data={tableData}
                                    sortColumn={sortColumn}
                                    sortDirection={sortDirection}
                                    onSort={handleSort}
                                    onRowClick={handleRowClick}
                                    visibleColumns={visibleColumns}
                                    availableCustomFields={availableCustomFields}
                                    columnOrder={columnOrder}
                                    onColumnReorder={handleColumnReorder}
                                    companyId={companyId}
                                    onLeadUpdate={handleLeadUpdate}
                                    loading={leadsLoading}
                                    refetching={isRefetching}
                                    onOpenImageModal={openImageUrlModal}
                                    onOpenContactModal={openContactModal}
                                    onEditAddress={openEditAddressModal}
                                    companyData={companyData}
                                    currentUserId={user?.userId}
                                    leadTypeFilter={leadTypeFilter}
                                    onArchiveLead={handleArchiveLead}
                                    onRestoreLead={handleRestoreLead}
                                    isArchivedView={archivedFilter}
                                    actionsOpenForLead={actionsOpenForLead}
                                    onActionsOpenChange={setActionsOpenForLead}
                                    page={page}
                                />
                                {leadsTotal > PAGE_SIZE && (
                                    <Pagination
                                        total={leadsTotal}
                                        limit={PAGE_SIZE}
                                        currentPage={page}
                                        onPageChange={onPageChange}
                                        siblingCount={1}
                                    />
                                )}
                            </div>
                        ) : (
                            <>
                                <GoogleMapsLoader>
                                    {(mapsIsLoaded) => (
                                        <div className={`leads-map-view-container${isMobile ? ' leads-mobile-map' : ''}`}>
                                                <div className="leads-map-wrapper">
                                                    <LeadsMap
                                                        leads={mappableLeads}
                                                        isMapLoaded={mapsIsLoaded}
                                                        hoverLeadId={selectedLeadId}
                                                        onLeadClick={handleMapPinClick}
                                                        onLeadHover={null}
                                                        rawMap={rawMap}
                                                        setRawMap={setRawMap}
                                                        mapType={mapType}
                                                        setMapType={setMapType}
                                                        innerWidth={innerWidth}
                                                        onViewportChange={handleViewportChange}
                                                        viewportFilterActive={viewportFilterActive}
                                                        initialBounds={initialBounds}
                                                        gestureHandling={isMobile ? 'greedy' : 'auto'}
                                                    />
                                                </div>
                                                {!isMobile && (
                                                <div className="leads-cards-wrapper" ref={cardsScrollRef}>
                                                    {!leadsInBoundsQuery.data && <Loading />}
                                                    {leadsInBoundsQuery.data && (
                                                        <div className="leads-cards-rail-header">
                                                            {isRefetching && <div className="leads-cards-refetch-bar" aria-hidden="true" />}
                                                            <span className="label-regular colour-secondary">
                                                                {mapTotalInBounds === 0
                                                                    ? 'No leads in this area — zoom out or pan to find more'
                                                                    : mapCapped
                                                                        ? `Showing ${MAP_VIEWPORT_LIMIT} of ${mapTotalInBounds}+ — zoom in to see more`
                                                                        : `${mapTotalInBounds} ${mapTotalInBounds === 1 ? 'lead' : 'leads'} in this area`}
                                                            </span>
                                                        </div>
                                                    )}
                                                    {/* Virtualized rail: only cards in/near the viewport mount;
                                                        the rest unmount so iOS Safari reclaims their decoded-image
                                                        bitmaps. The spacer reserves full scroll height; each row is
                                                        absolutely positioned via translateY. cardsScrollRef (the
                                                        wrapper) is the element scroll container on both breakpoints. */}
                                                    <div
                                                        className="leads-rail-virtual"
                                                        style={{ height: railVirtualizer.getTotalSize(), position: 'relative', width: '100%' }}
                                                    >
                                                        {railVirtualizer.getVirtualItems().map((vItem) => {
                                                            const lead = orderedMappableLeads[vItem.index];
                                                            if (!lead) return null;
                                                            return (
                                                                <div
                                                                    key={lead.leadId}
                                                                    data-index={vItem.index}
                                                                    ref={railVirtualizer.measureElement}
                                                                    className="leads-rail-vrow"
                                                                    style={{
                                                                        position: 'absolute',
                                                                        top: 0,
                                                                        left: 0,
                                                                        width: '100%',
                                                                        transform: `translateY(${vItem.start}px)`,
                                                                    }}
                                                                >
                                                                    <LeadCard
                                                                        lead={lead}
                                                                        isClicked={selectedLeadId === lead.leadId}
                                                                        onClick={handleLeadClick}
                                                                        onOpenImages={handleOpenImages}
                                                                    />
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                                )}
                                            </div>
                                    )}
                                </GoogleMapsLoader>
                            </>
                        )}
                        {/* Mobile tap-a-pin card — the tapped lead's LeadCard,
                            portaled to <body> and fixed above the bottom nav
                            (Airbnb pattern). Portaled (not docked inside the map)
                            because the Leads mobile map isn't full-screen — an
                            in-map absolute card would be clipped by the map's
                            overflow:hidden and could collide with the sticky bottom
                            nav on short screens. The X dismisses it; tapping the
                            card body still navigates to the lead. */}
                        {isMobile && selectedMapLead && !mobileOverlayActive && createPortal(
                            <div className="leads-map-card" role="dialog" aria-label="Selected lead">
                                <button
                                    type="button"
                                    className="leads-map-card-close"
                                    onClick={handleCloseMapCard}
                                    aria-label="Close lead card"
                                >
                                    <HugeiconsIcon icon={Cancel01Icon} color={colour.gray00} size={16} />
                                </button>
                                <LeadCard
                                    lead={selectedMapLead}
                                    isClicked={false}
                                    onClick={handleLeadClick}
                                    onOpenImages={handleOpenImages}
                                    navigateOnImageClick={isMobile}
                                />
                            </div>,
                            document.body
                        )}
                        {/* Floating Map/Table toggle (mobile only) — the mobile
                            stand-in for the desktop segmented control. Portaled to
                            document.body so the outlet's overflow-x:clip can't clip
                            its position:fixed (the same reason Layout.js keeps
                            SetupChecklistWidget a sibling of the outlet). Sits above
                            the sticky bottom MobileNavigation (see CSS). Hidden while
                            a photo viewer or the tap-a-pin card is open so the two
                            never fight for the bottom space. */}
                        {isMobile && !showWaitingState && !showProcessingState && !mobileOverlayActive && !selectedMapLead && createPortal(
                            <button
                                type="button"
                                className="leads-view-toggle-fab body-semibold"
                                onClick={toggleMobileView}
                                aria-label={tableView ? 'Show map' : 'Show list'}
                            >
                                {tableView ? (
                                    <>
                                        <HugeiconsIcon icon={Location01Icon} color={colour.gray00} size={16} />
                                        <span>Map</span>
                                    </>
                                ) : (
                                    <>
                                        <HugeiconsIcon icon={LeftToRightListBulletIcon} color={colour.gray00} size={16} />
                                        <span>List</span>
                                    </>
                                )}
                            </button>,
                            document.body
                        )}
                    </div>
                </div>
                <LeadsActivityRail open={railOpen} onToggle={toggleRail} />
                </div>
            {/* Image viewer overlays — opened from a map-view LeadCard image
                click. ScrollableImages = grid, ImageGallery = fullscreen
                swiper. Same flow PropertyCard uses in lead detail. */}
            {viewScrollableImages && imageViewerImages.length > 0 && (
                <ScrollableImages
                    images={imageViewerImages}
                    closeScrollableImages={closeScrollableImages}
                    imageClick={(index) => openImageGallery(index)}
                    condition={null}
                    imageLabels={imageViewerLabels}
                />
            )}
            {viewImageGallery && imageViewerImages.length > 0 && (
                <ImageGallery
                    images={imageViewerImages}
                    imageIndex={imageViewPropertyIndex}
                    setImageIndex={setImageIndex}
                    closeImageGallery={() => closeImageGallery(true)}
                    alt={imageViewProperty?.address?.streetAddress || 'Property image'}
                />
            )}
            {imageUrlModalOpen && (
                <>
                    <Modal
                    heading="Add Image Link"
                    subheading={null}
                    body={
                        <Suspense fallback={null}>
                            <ImageUrlModal
                                imageUrl={imageUrlModalValue}
                                setImageUrl={setImageUrlModalValue}
                                error={imageUrlModalValue !== '' && !isValidUrl(imageUrlModalValue)}
                            />
                        </Suspense>
                    }
                    type="basic"
                    cancelText="Cancel"
                    cancelButtonCTA={closeImageUrlModal}
                    actionButtonText="Save"
                    actionButtonCTA={saveImageUrl}
                    secondaryButtonText={null}
                    secondaryButtonCTA={null}
                    exitButtonCTA={closeImageUrlModal}
                    loading={imageUrlModalLoading}
                    actionButtonDisabled={!isValidUrl(imageUrlModalValue) || imageUrlModalLoading}
                    leftIcon={null}
                    rightIcon={null}
                    cancelLeftIcon={null}
                    cancelRightIcon={null}
                    customClass="image-url-modal"
                />
                </>
            )}
            {contactModalOpen && (
                <>
                    <Modal
                        heading="Unknown Contact"
                        subheading="We couldn't identify a main contact from the data we received for this lead. Review the raw data below and fill in the contact details manually."
                        body={
                            <Suspense fallback={null}>
                            <IdentifyContactModal
                                rawData={contactModalRawData}
                                values={contactModalValues}
                                setValues={setContactModalValues}
                                contacts={companyContacts}
                            />
                            </Suspense>
                        }
                        type="basic"
                        cancelText="Cancel"
                        cancelButtonCTA={closeContactModal}
                        actionButtonText="Save"
                        actionButtonCTA={saveContact}
                        secondaryButtonText={null}
                        secondaryButtonCTA={null}
                        exitButtonCTA={closeContactModal}
                        loading={contactModalLoading}
                        actionButtonDisabled={
                            contactModalLoading ||
                            (!contactModalValues.name && !contactModalValues.company && !contactModalValues.email && !contactModalValues.phone)
                        }
                        leftIcon={null}
                        rightIcon={null}
                        cancelLeftIcon={null}
                        cancelRightIcon={null}
                        customClass="identify-contact-modal"
                    />
                </>
            )}
            {createLeadModalOpen && (createLeadVerifyProperty === null || createLeadPreviewPending) && (
                <>
                    <Modal
                        heading="New lead"
                        subheading={createLeadPreviewPending
                            ? "Confirm we found the right property."
                            : (createLeadMode === 'address'
                                ? "Type an address to get started."
                                : "Paste a wholesaler text, Facebook post, or voicemail.")}
                        body={
                            <Suspense fallback={null}><CreateLeadModal
                                mode={createLeadMode}
                                setMode={handleCreateLeadModeChange}
                                value={createLeadText}
                                onChange={handleCreateLeadTextChange}
                                onSubmit={handleSubmitCreateLeadText}
                                onPick={handlePickCreateLeadSuggestion}
                                onPasteStart={handleCreateLeadPasteStart}
                                results={createLeadAddress.results}
                                isSearching={createLeadAddress.isSearching}
                                loading={createLeadLoading}
                                loadingMessage={createLeadLoadingMessage}
                                loadingSteps={createLeadLoadingSteps}
                                error={createLeadError}
                                preview={createLeadPreview}
                            /></Suspense>
                        }
                        type="basic"
                        cancelText={createLeadPreviewPending ? "Edit text" : "Cancel"}
                        cancelButtonCTA={createLeadPreviewPending ? handleEditCreateLeadText : closeCreateLeadModal}
                        actionButtonText="Continue"
                        actionButtonCTA={createLeadPreviewPending ? handleConfirmCreateLeadPreview : handleSubmitCreateLeadText}
                        secondaryButtonText={null}
                        secondaryButtonCTA={null}
                        exitButtonCTA={closeCreateLeadModal}
                        loading={createLeadLoading}
                        actionButtonDisabled={createLeadLoading || (!createLeadPreviewPending && createLeadText.trim().length === 0)}
                        leftIcon={null}
                        rightIcon={null}
                        cancelLeftIcon={null}
                        cancelRightIcon={null}
                        customClass="create-lead-modal"
                    />
                </>
            )}
            {createLeadVerifyProperty !== null && !createLeadPreviewPending && companyId && (
                <div
                    className="create-lead-verify-overlay"
                    ref={(el) => {
                        // Ensure VerifyProperty mounts scrolled to the top.
                        // Without this, when the form has an input that ends up
                        // focused on mount (browser default, or scrollIntoView
                        // from inside VerifyProperty), the overlay can land
                        // scrolled past the title section — looks like content
                        // is cut off.
                        if (el && el.scrollTop !== 0) el.scrollTop = 0;
                    }}
                >
                    <VerifyProperty
                        leadMode={true}
                        initialProperty={createLeadVerifyProperty}
                        initialCompanyId={companyId}
                        initialPropertyEstimates={null}
                        initialPropertyNotes={createLeadExtracted?.notes || ''}
                        initialRemodelLevel={null}
                        initialImageUrl={createLeadInitialImageUrl}
                        onLeadComplete={handleLeadComplete}
                        onLeadBack={handleLeadVerifyBack}
                        submitting={createLeadLoading}
                    />
                </div>
            )}
            {editAddressModalOpen && (
                <>
                    <Modal
                        heading="Edit Address"
                        subheading="Search for the correct address to re-run enrichment"
                        body={
                            <Suspense fallback={null}><EditAddressModal
                                currentAddress={editAddressCurrentAddress}
                                addressSearch={editAddressAutocomplete.query}
                                onAddressSearchChange={handleEditAddressSearchChange}
                                addressSearchResults={editAddressAutocomplete.results}
                                addressSearching={editAddressAutocomplete.isSearching}
                                addressSelected={editAddressSelected}
                                onSelectAddress={handleSelectEditAddress}
                                onClearAddress={handleClearEditAddress}
                                selectedAddressLabel={editAddressSelectedLabel}
                                error={editAddressError}
                            /></Suspense>
                        }
                        type="basic"
                        cancelText="Cancel"
                        cancelButtonCTA={closeEditAddressModal}
                        actionButtonText="Reprocess Lead"
                        actionButtonCTA={submitEditAddress}
                        secondaryButtonText={null}
                        secondaryButtonCTA={null}
                        exitButtonCTA={closeEditAddressModal}
                        loading={editAddressLoading}
                        actionButtonDisabled={!editAddressSelected || editAddressLoading}
                        leftIcon={null}
                        rightIcon={null}
                        cancelLeftIcon={null}
                        cancelRightIcon={null}
                        customClass="edit-address-modal"
                    />
                </>
            )}
            {portfolioModalOpen && (
                <>
                    <Modal
                        heading="Create Portfolio"
                        subheading="Group leads that are sold together for one price"
                        body={
                            <Suspense fallback={null}><CreatePortfolioModal
                                leads={leads}
                                selectedLeadIds={portfolioSelectedLeadIds}
                                selectedLeadsData={portfolioSelectedLeadsData}
                                onToggleLead={handleTogglePortfolioLead}
                                portfolioName={portfolioName}
                                setPortfolioName={setPortfolioName}
                                combinedAskingPrice={portfolioCombinedPrice}
                                setCombinedAskingPrice={setPortfolioCombinedPrice}
                                error={portfolioError}
                                searchTerm={portfolioSearchTerm}
                                setSearchTerm={setPortfolioSearchTerm}
                                searchResults={portfolioSearchResults}
                                searchLoading={portfolioSearchLoading}
                            /></Suspense>
                        }
                        type="basic"
                        cancelText="Cancel"
                        cancelButtonCTA={closePortfolioModal}
                        actionButtonText="Create Portfolio"
                        actionButtonCTA={submitCreatePortfolio}
                        secondaryButtonText={null}
                        secondaryButtonCTA={null}
                        exitButtonCTA={closePortfolioModal}
                        loading={portfolioLoading}
                        actionButtonDisabled={portfolioSelectedLeadIds.length < 2 || portfolioLoading}
                        leftIcon={null}
                        rightIcon={null}
                        cancelLeftIcon={null}
                        cancelRightIcon={null}
                        customClass="create-portfolio-modal"
                    />
                </>
            )}
            {filterModalOpen && (
                <>
                    <Modal
                        heading="Filters"
                        subheading="Filter by property details"
                        body={
                            <Suspense fallback={null}><LeadsFilterModal
                                priceMin={filterPriceMin}
                                setPriceMin={setFilterPriceMin}
                                priceMax={filterPriceMax}
                                setPriceMax={setFilterPriceMax}
                                filterState={filterStateName}
                                setFilterState={setFilterStateName}
                                filterCity={filterCity}
                                setFilterCity={setFilterCity}
                                bedsMin={filterBedsMin}
                                setBedsMin={setFilterBedsMin}
                                bedsMax={filterBedsMax}
                                setBedsMax={setFilterBedsMax}
                                bathsMin={filterBathsMin}
                                setBathsMin={setFilterBathsMin}
                                bathsMax={filterBathsMax}
                                setBathsMax={setFilterBathsMax}
                                sqftMin={filterSqftMin}
                                setSqftMin={setFilterSqftMin}
                                sqftMax={filterSqftMax}
                                setSqftMax={setFilterSqftMax}
                                yearBuiltMin={filterYearBuiltMin}
                                setYearBuiltMin={setFilterYearBuiltMin}
                                yearBuiltMax={filterYearBuiltMax}
                                setYearBuiltMax={setFilterYearBuiltMax}
                                availableStates={filteredStateResults}
                                availableCities={filteredCityResults}
                                onSelectState={handleSelectFilterState}
                                onSelectCity={handleSelectFilterCity}
                                onClearState={handleClearFilterState}
                                onClearCity={handleClearFilterCity}
                                spreadMin={filterSpreadMin}
                                setSpreadMin={setFilterSpreadMin}
                                maoVsAskingMax={filterMaoVsAskingMax}
                                setMaoVsAskingMax={setFilterMaoVsAskingMax}
                                financingPathOptions={FINANCING_PATH_OPTIONS}
                                availableFinancingPaths={availableFinancingPaths}
                                selectedFinancingPaths={filterFinancingPaths}
                                onToggleFinancingPath={handleToggleFilterFinancingPath}
                                dealScoreMin={filterDealScoreMin}
                                setDealScoreMin={setFilterDealScoreMin}
                                dateMin={filterDateMin}
                                setDateMin={setFilterDateMin}
                                dateMax={filterDateMax}
                                setDateMax={setFilterDateMax}
                                selectedPropertyTypes={filterPropertyType}
                                availablePropertyTypes={availablePropertyTypes}
                                onTogglePropertyType={handleToggleFilterPropertyType}
                                selectedAssignees={filterAssignees}
                                availableAssignees={companyData?.users || []}
                                currentUserId={user?.userId}
                                onToggleAssignee={handleToggleFilterAssignee}
                                selectedDealStatuses={filterDealStatuses}
                                onToggleDealStatus={handleToggleFilterDealStatus}
                                dealStatusOptions={dealStatusOptions}
                                auctionStatusOptions={AUCTION_STATUS_OPTIONS}
                                lienPositionOptions={LIEN_POSITION_OPTIONS}
                                availableAuctionStatuses={availableAuctionStatuses}
                                selectedAuctionStatuses={filterAuctionStatus}
                                onToggleAuctionStatus={handleToggleFilterAuctionStatus}
                                selectedLienPositions={filterLienPosition}
                                onToggleLienPosition={handleToggleFilterLienPosition}
                                auctionDateMin={filterAuctionDateMin}
                                setAuctionDateMin={setFilterAuctionDateMin}
                                auctionDateMax={filterAuctionDateMax}
                                setAuctionDateMax={setFilterAuctionDateMax}
                                selectedSenders={filterSenders}
                                availableSenders={availableSenders}
                                senderSearch={filterSenderSearch}
                                onSenderSearchChange={(e) => setFilterSenderSearch(e.target.value)}
                                onClearSenderSearch={handleClearFilterSenderSearch}
                                filteredSenderResults={filteredSenderResults}
                                onSelectSender={handleSelectFilterSender}
                                onRemoveSender={handleRemoveFilterSender}
                                availablePortfolios={availablePortfolios}
                                selectedPortfolios={filterPortfolios}
                                onTogglePortfolio={handleToggleFilterPortfolio}
                                archivedView={filterArchived}
                                setArchivedView={setFilterArchived}
                            /></Suspense>
                        }
                        type="basic"
                        cancelText={innerWidth <= 720 ? 'Clear' : 'Clear all filters'}
                        cancelButtonCTA={clearAllFiltersAndClose}
                        actionButtonText={innerWidth <= 720 ? 'Apply' : 'Apply Filters'}
                        actionButtonCTA={applyFilters}
                        secondaryButtonText={null}
                        secondaryButtonCTA={null}
                        exitButtonCTA={closeFilterModal}
                        loading={false}
                        actionButtonDisabled={false}
                        leftIcon={null}
                        rightIcon={null}
                        cancelLeftIcon={null}
                        cancelRightIcon={null}
                        customClass="leads-filter-modal"
                    />
                </>
            )}
            {archiveToast && (
                <div className="leads-archive-toast">
                    <span className="body-regular colour-white">
                        {archiveToast.address} archived
                    </span>
                    <button
                        type="button"
                        className="leads-archive-toast-undo body-regular"
                        onClick={handleUndoArchive}
                    >
                        Undo
                    </button>
                </div>
            )}
            {imageToast && (
                <div className="leads-archive-toast">
                    <span className="label-regular colour-white">{imageToast.message}</span>
                </div>
            )}
            {processingToast && (
                <div className="leads-archive-toast">
                    <span className="label-regular colour-white">{processingToast.message}</span>
                    {processingToast.variant === 'success' && processingToast.propertyId && (
                        <button
                            type="button"
                            className="leads-archive-toast-undo body-regular"
                            onClick={() => navigate(`/leads/${processingToast.propertyId}`)}
                        >
                            View deal
                        </button>
                    )}
                </div>
            )}
            {createLeadFailedToast && (
                <div className="leads-archive-toast">
                    <span className="label-regular colour-white">{createLeadFailedToast.message}</span>
                </div>
            )}
        </div>
    );
}

function MailGenericIcon() {
    return <HugeiconsIcon icon={Mail01Icon} size={22} color={colour.gray06} strokeWidth={1.8} />;
}

function LeadsWaitingState({ unipileAccounts, allowedFromNumbers, userPhone, supplementaryText = null, onCreateLead = null }) {
    const navigate = useNavigate();

    const emailConnected = Array.isArray(unipileAccounts) && unipileAccounts.length > 0;
    const isTwilio = Array.isArray(allowedFromNumbers) && allowedFromNumbers.length > 0;
    const isForward = !isTwilio && !!userPhone;
    const smsConnected = isTwilio || isForward;

    const hasGmail = emailConnected && unipileAccounts.some(a => (a.type || '').toUpperCase().startsWith('GOOGLE'));
    const hasOutlook = emailConnected && unipileAccounts.some(a => {
        const t = (a.type || '').toUpperCase();
        return t.startsWith('OUTLOOK') || t.startsWith('MICROSOFT');
    });

    const icons = [];
    if (hasGmail) icons.push(Gmail);
    else if (hasOutlook) icons.push(Outlook);
    else if (emailConnected) icons.push(MailGenericIcon);
    if (smsConnected) icons.push(isTwilio ? Twilio : Messages);
    if (icons.length === 0) icons.push(MailGenericIcon);

    let accountLabel = null;
    if (emailConnected) accountLabel = unipileAccounts[0]?.email || null;
    else if (isTwilio) accountLabel = formatPhoneNumber(allowedFromNumbers[0].replace(/^\+1/, ''));
    else if (isForward) accountLabel = formatPhoneNumber(userPhone.replace(/^\+1/, ''));

    let headline;
    if (emailConnected) headline = "Forward any real estate email to your connected inbox — it'll appear here within a minute.";
    else if (isTwilio) headline = "Have someone text your Twilio number with a property address — it'll appear here automatically.";
    else headline = "Forward a deal text to your Clozers number — it'll appear here within a minute.";

    return (
        <div className="leads-waiting-state">
            <div className="leads-waiting-icon-wrap">
                <div className="leads-waiting-icons">
                    {icons.map((Icon, i) => (
                        <span key={i} className="leads-waiting-icon"><Icon /></span>
                    ))}
                </div>
                {accountLabel && (
                    <span className="label-regular colour-secondary leads-waiting-account">{accountLabel}</span>
                )}
            </div>
            <div className="leads-waiting-text">
                <span className="body-semibold colour-secondary leads-waiting-subtext">{headline}</span>
                {supplementaryText && (
                    <span className="body-regular colour-secondary leads-waiting-subtext">{supplementaryText}</span>
                )}
            </div>
            {onCreateLead && (
                <button
                    type="button"
                    className="leads-create-primary-button body-bold"
                    onClick={onCreateLead}
                >
                    <HugeiconsIcon icon={PlusSignIcon} color={colour.gray00} size={14} />
                    <span>Create lead</span>
                </button>
            )}
            <div className="leads-waiting-manage-row">
                <div
                    className="text-button label-bold colour-secondary cursor-pointer"
                    onClick={() => navigate('/integrations')}
                >
                    Manage integrations →
                </div>
            </div>
        </div>
    );
}

function LeadsProcessingState({ lead, message = null }) {
    const address = lead?.address?.streetAddress || 'Property address';
    const city = lead?.address?.city;
    const state = lead?.address?.state;
    const cityState = [city, state].filter(Boolean).join(', ');
    const vc = lead?.verifiedCharacteristics;
    const details = [
        vc?.bedrooms != null ? `${vc.bedrooms} bd` : null,
        vc?.bathrooms != null ? `${vc.bathrooms} ba` : null,
        vc?.livingSquareFeet != null ? `${numberFormatter.format(vc.livingSquareFeet)} sqft` : null,
    ].filter(Boolean).join(' · ');

    return (
        <div className="leads-processing-state">
            <div className="leads-processing-icon-wrap">
                <div className="leads-processing-pulse" />
                <div className="leads-processing-pulse leads-processing-pulse-2" />
                <div className="leads-processing-dot" />
            </div>
            <div className="leads-processing-text">
                <span className="heading-small-bold colour-primary">{message || address}</span>
                {!message && cityState && (
                    <span className="body-regular colour-secondary">{cityState}</span>
                )}
                {!message && details && (
                    <span className="label-regular colour-secondary">{details}</span>
                )}
            </div>
            {!message && <span className="label-regular colour-secondary leads-processing-label">Analyzing deal...</span>}
        </div>
    );
}

export default Leads;
